import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { useAdCategoryStore } from "@/pages/ad-Categories/store/adCategoryStore";
import useGlobalStore from "@/store/globalStore";
import {
  Image as ImageIcon,
  X,
  Cloud,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Type,
  Square,
  Film,
  MousePointer,
  Pencil,
  Layers,
  GripVertical,
  Music,
  FileText,
} from "lucide-react";
import Button from "@/components/ui/Button";
import TextField from "@/components/ui/Textfield";
import TextareaField from "@/components/ui/Textareafield";
import Dropdown from "@/components/ui/DropDown";
import useAdBuilderStore from "./store/ad-builder-store";
import { computeSnap, type Guide, type Box } from "./alignment-guides";
import type { SubComponent, SaveComponentPayload, SaveLocationPayload } from "./store/ad-builder-store";
import {
  ChromeIcon,
  ChromeAvatar,
  ChromeGradient,
  iconGeometryFromSettings,
  iconSettingsFromDefaults,
  avatarSettingsFromDefaults,
  gradientSettingsFromDefaults,
  defaultRectFromSettings,
} from "@/components/ad-chrome/chrome-elements";
import { PhoneFrame, PHONE_FRAME_REACH } from "@/components/ad-chrome/phone-frame";
import { ExpandableText, expansionLayout } from "@/components/ad-chrome/expandable-text";
import CharacterCounter from "@/components/ui/CharacterCounter";

// Canvas element categories. These are the master component `name` values from
// the API (not `display_name`), so they stay stable if a display label is edited.
type ElementCategory =
  | "text"
  | "image"
  | "video"
  | "audio"
  | "button"
  | "shape"
  | "preview"
  | "attributes"
  // Added so a platform-exact preview can be drawn: glyphs, profile pictures
  // and the scrims behind overlaid captions. Seeded by ChromeComponentSeeder.
  | "icon"
  | "avatar"
  | "gradient";

interface Template {
  id: string;
  label: string;
  /** Master component `name` — stable key used for all rendering/matching. */
  category: ElementCategory;
  /** Master component `display_name` — for UI labels only. */
  categoryLabel: string;
}

interface MediaUpload {
  id: string;
  elementId: string;
  file: File | null;
  preview: string | null;
  slotIndex?: number; // For carousel items
}

interface FloatingMenu {
  type:
    | "text-settings"
    | "properties"
    | "shape-properties"
    | "image-properties"
    | "video-properties"
    | "chrome-properties";
  elementId: string;
  x: number;
  y: number;
}

// ─── Interface canvas dimensions ──────────────────────────────────────────────
const DEFAULT_CANVAS = { width: 375, height: 667, rounded: "rounded-[2rem]", isPhone: false };

// Max on-screen size of the design canvas before it is scaled down to fit.
const BUILDER_CANVAS_MAX_WIDTH = 820;
const BUILDER_CANVAS_MAX_HEIGHT = 720;

// Smallest an element may be resized to, in canvas pixels. Platform chrome is
// built from small parts — a 20px sound toggle, a 24px engagement glyph — so a
// larger floor would make those impossible to draw at their true size. The
// resize handles are drawn outside the element, so they stay usable this small.
const MIN_ELEMENT_SIZE = 8;

// Per-platform button styling. Kept in sync with the preview renderer in
// campaign-detail/components/ad-builder/ad-builder-canvas.tsx so a button looks
// the same while designing as it does in the preview.
const BUTTON_BRAND_DEFAULTS: Record<
  string,
  { bg?: string; color?: string; border?: string; radius?: string; shadow?: string }
> = {
  facebook: {
    bg: "#FFFFFF",
    color: "#1C2B33",
    border: "1px solid #DADDE1",
    radius: "6px",
  },
  instagram: {
    bg: "#FFFFFF",
    color: "#262626",
    border: "1px solid #DBDBDB",
    radius: "8px",
  },
  pinterest: {
    bg: "#E60023",
    color: "#FFFFFF",
    radius: "24px",
    shadow: "0 1px 4px rgba(0,0,0,0.15)",
  },
  linkedin: { bg: "#0A66C2", color: "#FFFFFF", radius: "20px" },
  x: { bg: "#000000", color: "#FFFFFF", radius: "9999px" },
};

const AdBuilder: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const adCategoryMappingId = searchParams.get("adCategoryMappingId");

  // Ad-builder store
  const {
    canvasComponents,
    isLoadingComponents,
    fetchCanvasComponents,
    loadCanvas,
    saveMaster,
    saveComponents,
    saveLocations,
    saveAdInfo,
    saveMedia,
    clearDirty,
    canvasMasterId,
    isSaving,
    loadedCanvasData,
    isLoadingCanvas,
  } = useAdBuilderStore();

  // Read selected interface/specs/blocks from adCategoryStore
  const {
    selectedInterface,
    selectedAdSpecs,
    selectedAdInfoBlocks,
    canvasBuilderData,
    setSelectedInterface,
    setSelectedAdSpecs,
    setSelectedAdInfoBlocks,
  } = useAdCategoryStore(
    useShallow((state) => ({
      selectedInterface: state.selectedInterface,
      selectedAdSpecs: state.selectedAdSpecs,
      selectedAdInfoBlocks: state.selectedAdInfoBlocks,
      canvasBuilderData: state.canvasBuilderData,
      setSelectedInterface: state.setSelectedInterface,
      setSelectedAdSpecs: state.setSelectedAdSpecs,
      setSelectedAdInfoBlocks: state.setSelectedAdInfoBlocks,
    })),
  );

  // Global toast
  const { showSuccess, setError } = useGlobalStore(
    useShallow((state) => ({
      showSuccess: state.showSuccess,
      setError: state.setError,
    })),
  );

  // Derive canvas dimensions directly from the API canvas_info of the selected interface
  const canvasDimensions = useMemo(() => {
    const found = selectedInterface
      ? canvasBuilderData?.interface_types?.find(
          (t: any) => t.id === selectedInterface,
        )
      : undefined;
    if (found?.canvas_info?.width && found?.canvas_info?.height) {
      // The mobile interface is drawn inside an iPhone frame, which supplies
      // the rounded screen edge itself.
      const isPhone = found.name === "mobile";
      const rounded = isPhone ? "" : "rounded-lg";
      return { width: found.canvas_info.width, height: found.canvas_info.height, rounded, isPhone };
    }

    // The interface list only arrives via Step 5. Opened directly (a link or a
    // refresh) the builder would fall back to 375x667, smaller than the canvas
    // was designed at, so its elements spilled out and the next save wrote the
    // wrong size. The saved canvas records its own size, so use that instead.
    const saved = loadedCanvasData?.canvas_master?.canvas_info_aggregated;
    if (saved?.canvasWidth && saved?.canvasHeight) {
      // Only the mobile interface is portrait and phone-width.
      const isPhone = saved.canvasWidth <= 500 && saved.canvasHeight > saved.canvasWidth;
      return {
        width: saved.canvasWidth,
        height: saved.canvasHeight,
        rounded: isPhone ? "" : "rounded-lg",
        isPhone,
      };
    }

    return DEFAULT_CANVAS;
  }, [selectedInterface, canvasBuilderData, loadedCanvasData]);

  // Scale the design canvas down to fit the editor viewport. Desktop (1440x900)
  // and preview (1920x1080) interfaces overflow the screen at 1:1, so without
  // this most of the canvas would be unreachable while designing. Never scales
  // up — a small canvas (e.g. mobile 390x844) still renders at true size.
  const canvasScale = useMemo(
    () =>
      Math.min(
        1,
        BUILDER_CANVAS_MAX_WIDTH / canvasDimensions.width,
        BUILDER_CANVAS_MAX_HEIGHT / canvasDimensions.height,
      ),
    [canvasDimensions],
  );

  // Alignment guides shown while an element is being dragged.
  const [activeGuides, setActiveGuides] = useState<Guide[]>([]);

  // "… more" texts expanded on the canvas and the extra height each needs.
  // Display only: what is below an expanded text is drawn pushed down, but its
  // saved position never changes.
  const [expandedTexts, setExpandedTexts] = useState<Record<string, boolean>>({});
  const [textExtras, setTextExtras] = useState<Record<string, number>>({});
  const measureText = useCallback((id: string, extra: number) => {
    setTextExtras((prev) => (prev[id] === extra ? prev : { ...prev, [id]: extra }));
  }, []);

  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [canvasElements, setCanvasElements] = useState<any[]>([]);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [floatingMenu, setFloatingMenu] = useState<FloatingMenu | null>(null);
  const [draggedTemplate, setDraggedTemplate] = useState<Template | null>(null);
  const [showLayersPanel, setShowLayersPanel] = useState(true);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Content form state - dynamic values keyed by sub-component key
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [mediaUploads, setMediaUploads] = useState<MediaUpload[]>([]);

  // Layer reorder drag state
  const [draggedLayerIndex, setDraggedLayerIndex] = useState<number | null>(
    null,
  );
  const [dragOverLayerIndex, setDragOverLayerIndex] = useState<number | null>(
    null,
  );

  // Fetch API data and load existing canvas on mount
  useEffect(() => {
    fetchCanvasComponents();
    if (adCategoryMappingId) {
      loadCanvas(adCategoryMappingId).catch(() => {
        // No canvas yet — that's fine, user will create one on save
      });
    }
  }, [fetchCanvasComponents, loadCanvas, adCategoryMappingId]);

  // Hydrate canvas elements and step 5 selections from loaded canvas data.
  // Runs once per loaded canvas: this effect depends on `canvasComponents`,
  // which arrives separately, and re-running it would overwrite the user's
  // in-progress edits with the saved snapshot.
  const hydratedCanvasIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoadingCanvas || !loadedCanvasData) return;
    // Wait for the component hierarchy — the form keys are resolved from it.
    if (canvasComponents.length === 0) return;

    const canvasId = loadedCanvasData.canvas_master?.id ?? null;
    if (canvasId && hydratedCanvasIdRef.current === canvasId) return;
    hydratedCanvasIdRef.current = canvasId;

    const canvasInfo = loadedCanvasData.canvas_master?.canvas_info_aggregated ?? {};

    // Restore step 5 selections saved in canvas_info_aggregated
    if (canvasInfo.selectedInterface) {
      setSelectedInterface(canvasInfo.selectedInterface);
    }
    if (Array.isArray(canvasInfo.selectedAdSpecs)) {
      setSelectedAdSpecs(canvasInfo.selectedAdSpecs);
    }
    if (Array.isArray(canvasInfo.selectedAdInfoBlocks)) {
      setSelectedAdInfoBlocks(canvasInfo.selectedAdInfoBlocks);
    }

    // Hydrate canvas elements from saved components
    if (!loadedCanvasData.components?.length) return;

    // Resolve the interface name whose locations we should hydrate from.
    // `canvasBuilderData` loads independently of the canvas, so this can be
    // unavailable on the first pass — hence the id-match fallback below.
    const savedInterfaceName = canvasInfo.selectedInterface
      ? canvasBuilderData?.interface_types?.find(
          (t: any) => t.id === canvasInfo.selectedInterface,
        )?.name
      : undefined;

    const hydrated = loadedCanvasData.components.map((component: any) => {
      const locations = component.locations ?? {};
      // Prefer the resolved interface name; otherwise match the saved interface
      // by id, and only then fall back to whatever location exists. Without
      // this a mobile-designed canvas hydrated to `locations["desktop"]`,
      // found nothing, and silently reset every element to its default box.
      const locEntry =
        (savedInterfaceName ? locations[savedInterfaceName] : undefined) ??
        Object.values(locations).find(
          (l: any) => l?.interface_id === canvasInfo.selectedInterface,
        ) ??
        Object.values(locations)[0];
      const loc = (locEntry as any)?.location_json;
      const agg = component.aggregated_json ?? {};

      // Collect location DB ids keyed by interface name for use on update
      const locationIds: Record<string, string> = {};
      if (component.locations) {
        Object.entries(component.locations).forEach(([iface, locData]: [string, any]) => {
          if (locData?.id) locationIds[iface] = locData.id;
        });
      }

      return {
        id: `element-${component.id}`,
        dbId: component.id,
        locationIds,
        layerName: component.canvas_component_sub_master_display_name,
        label: agg.label ?? component.canvas_component_sub_master_display_name,
        // Layers-panel eye: builder-only, always starts visible and is never saved
        visible: true,
        templateId: component.canvas_component_sub_master_name,
        category: component.canvas_component_master_name,
        x: loc?.x ?? 100,
        y: loc?.y ?? 100,
        width: loc?.width ?? 200,
        height: loc?.height ?? 100,
        content: agg.content,
        // Uploaded media keyed by slot index — used to rebuild upload slots
        media: agg.media,
        textSettings: agg.textSettings ? {
          fontSize: agg.textSettings.fontSize,
          font: agg.textSettings.fontFamily,
          bold: agg.textSettings.fontWeight === "bold",
          italic: agg.textSettings.fontStyle === "italic",
          underline: agg.textSettings.textDecoration?.includes("underline") ?? false,
          strikethrough: agg.textSettings.textDecoration?.includes("line-through") ?? false,
          color: agg.textSettings.color ?? "#000000",
          textAlign: agg.textSettings.textAlign,
          lineHeight: agg.textSettings.lineHeight,
          letterSpacing: agg.textSettings.letterSpacing,
          textTransform: agg.textSettings.textTransform,
          opacity: agg.textSettings.opacity,
        } : undefined,
        shapeSettings: agg.shapeSettings,
        buttonSettings: agg.buttonSettings,
        iconGeometry: agg.iconGeometry,
        iconSettings: agg.iconSettings,
        avatarSettings: agg.avatarSettings,
        gradientSettings: agg.gradientSettings,
        slotCount: agg.slotCount,
        properties: agg.properties ?? {
          showInTemplateOnly: false,
          showInInfoOnly: false,
          showCharacterCount: false,
          editable: true,
        },
      };
    });

    setCanvasElements(hydrated);

    // Rebuild the media upload slots for saved media elements. Slots only
    // existed in local state from `addElementToCanvas`, so on reload the
    // element reappeared on the canvas but its Media section was empty.
    // Previews are restored from `aggregated_json.media`, which the upload
    // endpoint writes keyed by slot index.
    const rebuiltUploads: MediaUpload[] = [];
    hydrated.forEach((el: any) => {
      if (!["image", "video", "audio", "preview", "avatar"].includes(el.category))
        return;

      const isMultiSlot =
        el.templateId?.includes("carousel") ||
        el.templateId?.includes("playlist");
      const savedMedia = el.media ?? {};
      // Keep every saved slot even if the element grew beyond the default count
      const savedIndexes = Object.keys(savedMedia).map((k) => Number(k));
      const slotsCount = Math.max(
        el.slotCount ?? (isMultiSlot ? 5 : 1),
        savedIndexes.length > 0 ? Math.max(...savedIndexes) + 1 : 0,
      );

      for (let i = 0; i < slotsCount; i++) {
        const saved = savedMedia[i] ?? savedMedia[String(i)];
        rebuiltUploads.push({
          id: `upload-${el.id}-${i}`,
          elementId: el.id,
          file: null,
          preview: saved?.url ?? null,
          slotIndex: isMultiSlot ? i : undefined,
        });
      }
    });
    setMediaUploads(rebuiltUploads);

    // Seed the form below the canvas from the saved content. Keyed by element
    // id, not by sub-component key: a canvas can hold two elements of the same
    // sub-type (e.g. two URL fields), and a shared key would make them
    // overwrite each other and edit in lockstep.
    const seededValues: Record<string, string> = {};
    hydrated.forEach((el: any) => {
      if (el.content == null || el.content === "") return;
      seededValues[el.id] = el.content;
    });
    if (Object.keys(seededValues).length > 0) {
      // Saved content wins: this runs on load, so `prev` holds defaults rather
      // than user edits, and letting it win would re-blank the seeded fields.
      setFormValues((prev) => ({ ...prev, ...seededValues }));
    }
  }, [loadedCanvasData, isLoadingCanvas, canvasBuilderData, canvasComponents]);

  // ── Save all canvas data ──────────────────────────────────────────────────
  const saveAll = useCallback(
    async (status: "draft" | "completed") => {
      if (!adCategoryMappingId) {
        setError("No ad category mapping ID found. Please navigate from Step 5.");
        return;
      }
      try {
        // 1. Save / update canvas master
        const masterId = await saveMaster(adCategoryMappingId, status, {
          canvasWidth: canvasDimensions.width,
          canvasHeight: canvasDimensions.height,
          status,
          selectedInterface,
          selectedAdSpecs,
          selectedAdInfoBlocks,
        });

        // 2. Build component payloads — map canvas elements to API shape
        const componentPayloads: SaveComponentPayload[] = canvasElements
          .map((el) => {
            // Find the master and sub-master from the hierarchy
            const masterComponent = canvasComponents.find(
              (c) => c.name === el.category,
            );
            const subComponent = masterComponent?.sub_components.find(
              (s) => s.name === el.templateId,
            );
            if (!masterComponent || !subComponent) return null;

            const aggregated_json: Record<string, any> = {
              label: el.label,
            };
            if (el.content !== undefined) aggregated_json.content = el.content;
            if (el.properties) aggregated_json.properties = el.properties;
            // Carry previously uploaded media through. The payload is rebuilt
            // from scratch each save, so omitting this would drop the media
            // URLs the upload endpoint wrote into aggregated_json.
            if (el.media) aggregated_json.media = el.media;
            if (el.textSettings) {
              aggregated_json.textSettings = {
                fontSize: el.textSettings.fontSize,
                fontFamily: el.textSettings.font ?? undefined,
                fontWeight: el.textSettings.bold ? "bold" : "normal",
                fontStyle: el.textSettings.italic ? "italic" : "normal",
                textDecoration: [
                  el.textSettings.underline ? "underline" : "",
                  el.textSettings.strikethrough ? "line-through" : "",
                ].filter(Boolean).join(" ") || undefined,
                color: el.textSettings.color,
                textAlign: el.textSettings.textAlign,
                lineHeight: el.textSettings.lineHeight,
                letterSpacing: el.textSettings.letterSpacing,
                textTransform: el.textSettings.textTransform,
                opacity: el.textSettings.opacity,
              };
            }
            // Shape/button settings are written with the same key names the
            // preview renderer reads, so the canvas round-trips unchanged.
            if (el.shapeSettings) {
              aggregated_json.shapeSettings = {
                fill: el.shapeSettings.fill,
                opacity: el.shapeSettings.opacity,
                borderRadius: el.shapeSettings.borderRadius,
                borderWidth: el.shapeSettings.borderWidth,
                borderColor: el.shapeSettings.borderColor,
                background: el.shapeSettings.background,
                overlay: el.shapeSettings.overlay,
              };
            }
            if (el.buttonSettings) {
              aggregated_json.buttonSettings = {
                backgroundColor: el.buttonSettings.backgroundColor,
                textColor: el.buttonSettings.textColor,
                text: el.buttonSettings.text,
                fontSize: el.buttonSettings.fontSize,
                fontWeight: el.buttonSettings.fontWeight,
                borderRadius: el.buttonSettings.borderRadius,
                borderColor: el.buttonSettings.borderColor,
                shadow: el.buttonSettings.shadow,
                icon: el.buttonSettings.icon,
              };
            }
            // Icon/avatar/gradient settings round-trip verbatim. The glyph's
            // geometry is saved with the element, not looked up from the
            // catalogue at render time, so an existing canvas keeps its
            // original glyph if the seeded catalogue is later changed.
            if (el.iconGeometry) aggregated_json.iconGeometry = el.iconGeometry;
            if (el.iconSettings) aggregated_json.iconSettings = el.iconSettings;
            if (el.avatarSettings)
              aggregated_json.avatarSettings = el.avatarSettings;
            if (el.gradientSettings)
              aggregated_json.gradientSettings = el.gradientSettings;
            if (el.slotCount) aggregated_json.slotCount = el.slotCount;

            const payload: SaveComponentPayload = {
              canvas_component_master_id: masterComponent.id,
              canvas_component_sub_master_id: subComponent.id,
              aggregated_json,
            };
            // Include existing DB id if available (for updates)
            if (el.dbId) payload.id = el.dbId;
            return payload;
          })
          .filter(Boolean) as SaveComponentPayload[];

        if (componentPayloads.length === 0) {
          showSuccess(
            status === "draft"
              ? "Progress saved (no components)."
              : "Template saved (no components).",
          );
          return;
        }

        // 3. Save components — get back DB ids
        const savedComponents = await saveComponents(masterId, componentPayloads);

        // Build a map: canvas element id → saved component DB id
        // For elements that already had a dbId (updates), use the response record
        // that matches by id; for new elements, match positionally within new-only items.
        const elementToDbId: Record<string, string> = {};
        // Pre-seed with existing dbIds so updates are resolved first
        canvasElements.forEach((el) => {
          if (el.dbId) elementToDbId[el.id] = el.dbId;
        });
        // For newly created elements (no dbId), match against savedComponents that are new
        const newElements = canvasElements.filter((el) => !el.dbId && componentPayloads.find((p) => !p.id));
        const newSaved = savedComponents.filter((sc: any) => !componentPayloads.find((p) => p.id === sc.id));
        newElements.forEach((el, i) => {
          if (newSaved[i]) elementToDbId[el.id] = newSaved[i].id;
        });

        // Persist DB ids back onto canvas elements for future updates
        setCanvasElements((prev) =>
          prev.map((el) =>
            elementToDbId[el.id] ? { ...el, dbId: elementToDbId[el.id] } : el,
          ),
        );

        // Derive interface name for location id lookup
        const currentInterfaceName =
          canvasBuilderData?.interface_types?.find((t: any) => t.id === selectedInterface)?.name ?? "";

        // 4. Save locations (using selectedInterface) & ad info in parallel
        const locationPayloads: SaveLocationPayload[] = canvasElements
          .map((el, idx) => {
            const masterComponent = canvasComponents.find(
              (c) => c.name === el.category,
            );
            const subComponent = masterComponent?.sub_components.find(
              (s) => s.name === el.templateId,
            );
            const dbId = elementToDbId[el.id] || el.dbId;
            if (!masterComponent || !subComponent || !dbId) return null;

            const payload: SaveLocationPayload = {
              canvas_component_master_id: masterComponent.id,
              canvas_component_sub_master_id: subComponent.id,
              canvas_component_info_id: dbId,
              interface_id: selectedInterface,
              location_json: {
                x: Math.round(el.x),
                y: Math.round(el.y),
                width: Math.round(el.width),
                height: Math.round(el.height),
                z_index: idx + 1,
              },
            };

            // Include existing location DB id so the backend updates instead of creating
            const existingLocationId = el.locationIds?.[currentInterfaceName];
            if (existingLocationId) payload.id = existingLocationId;

            return payload;
          })
          .filter(Boolean) as SaveLocationPayload[];

        const savedLocations = await saveLocations(masterId, locationPayloads);

        // Persist returned location ids back onto canvas elements for future updates
        if (Array.isArray(savedLocations) && currentInterfaceName) {
          setCanvasElements((prev) =>
            prev.map((el) => {
              const dbId = elementToDbId[el.id] || el.dbId;
              const savedLoc = savedLocations.find((l: any) => l.canvas_component_info_id === dbId);
              if (!savedLoc) return el;
              return {
                ...el,
                locationIds: { ...(el.locationIds ?? {}), [currentInterfaceName]: savedLoc.id },
              };
            }),
          );
        }

        // 5. Upload media files sequentially
        for (const upload of mediaUploads) {
          if (!upload.file) continue;
          const dbId = elementToDbId[upload.elementId] ||
            canvasElements.find((el) => el.id === upload.elementId)?.dbId;
          if (!dbId) continue;
          await saveMedia(masterId, dbId, upload.file, upload.slotIndex ?? 0);
        }

        clearDirty("master");
        showSuccess(
          status === "draft" ? "Progress saved successfully." : "Template saved successfully.",
        );
      } catch (err: any) {
        setError(err?.message || "Save failed. Please try again.");
      }
    },
    [
      adCategoryMappingId,
      canvasElements,
      canvasComponents,
      mediaUploads,
      selectedInterface,
      selectedAdSpecs,
      selectedAdInfoBlocks,
      canvasDimensions,
      canvasBuilderData,
      saveMaster,
      saveComponents,
      saveLocations,
      saveMedia,
      clearDirty,
      showSuccess,
      setError,
    ],
  );

  // Set default category when data loads
  useEffect(() => {
    if (canvasComponents.length > 0 && !selectedCategory) {
      setSelectedCategory(canvasComponents[0].name);
    }
  }, [canvasComponents, selectedCategory]);

  // Derive categories from API data — `name` is the key, `display_name` the label
  /**
   * Boxes a dragged element can align to: every other visible element.
   *
   * Hidden elements are excluded so an invisible layer cannot pull the drag
   * toward a guide the user cannot see.
   */
  const textLayout = useMemo(
    () =>
      expansionLayout(
        canvasElements
          .filter((el) => el.visible !== false)
          .map((el) => ({
            id: el.id,
            x: el.x,
            y: el.y,
            width: el.width,
            height: el.height,
            pinned: Boolean(el.properties?.pinned),
            container: el.category === "shape" || el.category === "gradient",
          })),
        textExtras,
      ),
    [canvasElements, textExtras],
  );

  const alignmentBoxes = useMemo(
    () =>
      canvasElements
        .filter((el) => el.visible !== false)
        .map((el) => ({
          id: el.id,
          x: el.x,
          y: el.y,
          width: el.width,
          height: el.height,
        })),
    [canvasElements],
  );

  const categories = canvasComponents.map((c) => ({
    name: c.name,
    label: c.display_name,
  }));

  // Get templates from API data for selected category
  const getCategoryTemplates = (categoryName: string): Template[] => {
    const component = canvasComponents.find((c) => c.name === categoryName);
    if (!component) return [];
    return component.sub_components
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((sub) => ({
        id: sub.name,
        label: sub.display_name,
        category: component.name as ElementCategory,
        categoryLabel: component.display_name,
      }));
  };

  // Get sub-component data by name from API
  const getSubComponent = (
    categoryName: string,
    subName: string,
  ): SubComponent | undefined => {
    const component = canvasComponents.find((c) => c.name === categoryName);
    return component?.sub_components.find((s) => s.name === subName);
  };

  // Helper: collect all sub-components of specific categories from canvas
  // elements, paired with the element they came from. The pairing is what lets
  // the form below the canvas write straight into that element's content.
  const getSubComponentsByCategory = (
    componentCategory: string,
  ): Array<{ sub: SubComponent; element: any }> => {
    const pairs: Array<{ sub: SubComponent; element: any }> = [];
    canvasElements.forEach((el) => {
      const sub = getSubComponent(el.category, el.templateId);
      if (sub && sub.component_category === componentCategory) {
        pairs.push({ sub, element: el });
      }
    });
    return pairs;
  };

  /**
   * The label shown for a content field. `settings.label` is the business-facing
   * name (e.g. "Description"), so it wins over the structural display name
   * (e.g. "URL") — the same string is then used by the form, the Properties
   * panel and the canvas placeholder so they never disagree.
   */
  const getFieldLabel = (sub?: SubComponent, fallback?: string): string =>
    sub?.settings?.label || sub?.display_name || fallback || "";

  // Update a content field: keep the keyed form value and the canvas element's
  // content in lockstep so typing below the canvas updates the canvas live.
  // Values are keyed by element id so two fields of the same sub-type stay
  // independent.
  const updateFormValue = (key: string, value: string, elementId?: string) => {
    setFormValues((prev) => ({ ...prev, [elementId ?? key]: value }));
    if (elementId) {
      updateElementProperty(elementId, "content", value);
    }
  };

  // Get button settings from API data.
  // Key names match what the preview renderer reads off aggregated_json.
  const getButtonSettings = (templateId: string) => {
    const sub = getSubComponent("button", templateId);
    if (sub?.settings) {
      return {
        backgroundColor: sub.settings.bg || "#333333",
        textColor: sub.settings.text_color || "#FFFFFF",
        text: sub.settings.label || "Click Here",
        icon: "",
        borderColor: sub.settings.border_color,
        borderRadius: sub.settings.border_radius || "8px",
        shadow: sub.settings.shadow,
        fontSize: 14,
        fontWeight: "600",
      };
    }
    return {
      backgroundColor: "#333333",
      textColor: "#FFFFFF",
      text: "Click Here",
      icon: "",
      borderRadius: "8px",
      fontSize: 14,
      fontWeight: "600",
    };
  };

  const addElementToCanvas = (
    template: Template,
    dropX?: number,
    dropY?: number,
  ) => {
    // Backend `settings_json` for this sub-component. Icon/avatar/gradient
    // elements take their geometry, styling and drop size from it, so a new
    // glyph or platform is a seeder row rather than a frontend change.
    const sub = getSubComponent(template.category, template.id);
    const subSettings = sub?.settings as Record<string, any> | undefined;
    // Glyphs are square and avatars small; without a seeded size they would
    // land as a 200x100 box and have to be resized by hand every time.
    const seededRect = defaultRectFromSettings(subSettings);

    const newElement = {
      id: `element-${Date.now()}`,
      layerName: template.label,
      visible: true,
      templateId: template.id,
      label: template.label,
      category: template.category,
      x: dropX ?? 100,
      y: dropY ?? 100,
      width: seededRect?.width ?? (template.category === "button" ? 200 : 200),
      height: seededRect?.height ?? (template.category === "button" ? 48 : 100),
      content: template.category === "text" ? "Content Title Goes Here" : "",
      properties: {
        showInTemplateOnly: false,
        showInInfoOnly: false,
        showCharacterCount: false,
        editable: true,
      },
      textSettings:
        template.category === "text"
          ? {
              font: "",
              fontSize: 12,
              bold: false,
              italic: false,
              underline: false,
              strikethrough: false,
              color: "#000000",
            }
          : undefined,
      shapeSettings:
        template.category === "shape"
          ? {
              background: true,
              overlay: true,
              fill: "#EA33C0",
              opacity: 1,
              borderRadius: template.id === "circle" ? "50%" : 0,
              borderWidth: 0,
              borderColor: "transparent",
            }
          : undefined,
      buttonSettings:
        template.category === "button"
          ? getButtonSettings(template.id)
          : undefined,
      // Icon geometry is copied onto the element rather than looked up at
      // render time, so a saved canvas keeps rendering its original glyph even
      // if the seeded catalogue is later edited.
      iconGeometry:
        template.category === "icon"
          ? iconGeometryFromSettings(subSettings)
          : undefined,
      iconSettings:
        template.category === "icon"
          ? iconSettingsFromDefaults(subSettings)
          : undefined,
      avatarSettings:
        template.category === "avatar"
          ? avatarSettingsFromDefaults(subSettings)
          : undefined,
      gradientSettings:
        template.category === "gradient"
          ? gradientSettingsFromDefaults(subSettings)
          : undefined,
      // How many slots this element has. Saved with the element because the
      // canvas `show` endpoint does not return the sub-component settings, so
      // the preview cannot look the count up — without it a carousel with no
      // uploads yet has no slides to page through.
      slotCount:
        typeof subSettings?.slots === "number" && subSettings.slots > 0
          ? subSettings.slots
          : undefined,
    };

    setCanvasElements([...canvasElements, newElement]);
    setSelectedElement(newElement.id);

    // Create media upload slots for Image/Video/Audio/Preview elements.
    // Avatars are image-backed too, so they get a single slot.
    if (
      template.category === "image" ||
      template.category === "video" ||
      template.category === "audio" ||
      template.category === "preview" ||
      template.category === "avatar"
    ) {
      const isCarousel = template.id.includes("carousel");
      const isPlaylist = template.id.includes("playlist");
      // The backend declares the slot count in `settings.slots`; fall back to
      // the old hardcoded 5 only if a sub-component omits it.
      const slotsCount =
        typeof subSettings?.slots === "number" && subSettings.slots > 0
          ? subSettings.slots
          : isCarousel || isPlaylist
            ? 5
            : 1;

      const newUploads: MediaUpload[] = [];
      for (let i = 0; i < slotsCount; i++) {
        newUploads.push({
          id: `upload-${newElement.id}-${i}`,
          elementId: newElement.id,
          file: null,
          preview: null,
          slotIndex: isCarousel || isPlaylist ? i : undefined,
        });
      }

      setMediaUploads([...mediaUploads, ...newUploads]);
    }
  };

  const handleDragStart = (template: Template) => {
    setDraggedTemplate(template);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedTemplate && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      // getBoundingClientRect reports the scaled box, so divide by the scale to
      // get a position in the canvas's own (unscaled) coordinate space.
      const x = (e.clientX - rect.left) / canvasScale;
      const y = (e.clientY - rect.top) / canvasScale;
      addElementToCanvas(draggedTemplate, x - 100, y - 50);
      setDraggedTemplate(null);
    }
  };

  const openFloatingMenu = (
    type: FloatingMenu["type"],
    elementId: string,
    x: number,
    y: number,
  ) => {
    setFloatingMenu({ type, elementId, x, y });
  };

  const closeFloatingMenu = () => {
    setFloatingMenu(null);
  };

  const updateElementProperty = (
    elementId: string,
    path: string,
    value: any,
  ) => {
    setCanvasElements((prev) =>
      prev.map((el) => {
        if (el.id === elementId) {
          const keys = path.split(".");
          if (keys.length === 1) {
            return { ...el, [keys[0]]: value };
          } else if (keys.length === 2) {
            return {
              ...el,
              [keys[0]]: {
                ...el[keys[0]],
                [keys[1]]: value,
              },
            };
          }
        }
        return el;
      }),
    );
  };

  const updateElementMultipleProperties = (
    elementId: string,
    updates: Record<string, any>,
  ) => {
    setCanvasElements((prev) =>
      prev.map((el) => {
        if (el.id === elementId) {
          return { ...el, ...updates };
        }
        return el;
      }),
    );
  };

  const selectedElementData = canvasElements.find(
    (el) => el.id === selectedElement,
  );

  const handleMediaUpload = (
    uploadId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setMediaUploads(
        mediaUploads.map((upload) =>
          upload.id === uploadId
            ? { ...upload, file, preview: URL.createObjectURL(file) }
            : upload,
        ),
      );
    }
  };

  const removeMediaUpload = (uploadId: string) => {
    setMediaUploads(
      mediaUploads.map((upload) =>
        upload.id === uploadId
          ? { ...upload, file: null, preview: null }
          : upload,
      ),
    );
  };

  const removeCanvasElement = (elementId: string) => {
    // Remove element from canvas
    setCanvasElements(canvasElements.filter((el) => el.id !== elementId));
    // Remove associated media uploads
    setMediaUploads(
      mediaUploads.filter((upload) => upload.elementId !== elementId),
    );
    // Clear selection if this element was selected
    if (selectedElement === elementId) {
      setSelectedElement(null);
    }
    // Close floating menu if open
    closeFloatingMenu();
  };

  const toggleLayerVisibility = (elementId: string) => {
    setCanvasElements((prev) =>
      prev.map((el) =>
        el.id === elementId ? { ...el, visible: !el.visible } : el,
      ),
    );
  };

  const renameLayer = (elementId: string, newName: string) => {
    setCanvasElements((prev) =>
      prev.map((el) =>
        el.id === elementId ? { ...el, layerName: newName } : el,
      ),
    );
  };

  // Layer reorder: move element from one position to another
  const reorderLayers = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setCanvasElements((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  // Get media uploads grouped by element
  const getMediaUploadsByCategory = () => {
    const imageUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "image";
    });

    const videoUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "video";
    });

    const audioUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "audio";
    });

    const previewUploads = mediaUploads.filter((upload) => {
      const element = canvasElements.find((el) => el.id === upload.elementId);
      return element && element.category === "preview";
    });

    return { imageUploads, videoUploads, audioUploads, previewUploads };
  };

  return (
    <div className="flex flex-col h-screen bg-[#EDEDED]">
      {/* Top Header */}
      <div className="bg-[#e7e7e70e] px-8 pt-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-black mb-2">
              Create Ad Type Template
            </h1>
          </div>
          <div className="flex gap-3">
            <Button type="secondary" enableHover={false} onClick={() => navigate(-1)}>
              Previous
            </Button>
            <Button
              type="secondary"
              leadingIcon={<Cloud size={18} />}
              onClick={() => saveAll("draft")}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Progress"}
            </Button>
            <Button
              type="primary"
              onClick={() => saveAll("completed")}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <div className="bg-[#e7e7e70e] px-8 py-4">
        <p className="text-black">
          Define Template Attributes for {canvasBuilderData?.ad_type?.name ?? "[Ad Type]"}
        </p>
      </div>

      {/* Pink Top Bar */}
      <hr className="border-[#EA33C0]" />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar — scrolls independently: the master menu list is
            backend-driven and grows as new element types are seeded, so it can
            exceed the viewport. `shrink-0` keeps the fixed width from being
            squeezed by the canvas column. */}
        <div className="w-40 shrink-0 bg-[#dedede72] p-4 overflow-y-auto">
          {categories.map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`w-full text-left px-3 py-3 mb-1 transition-colors text-base relative ${
                selectedCategory === category.name
                  ? "text-[#EA33C0] font-semibold bg-transparent pl-2.5"
                  : "text-black hover:bg-gray-200 font-normal"
              }`}
            >
              {selectedCategory === category.name && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-[50%] w-[3px] bg-[#EA33C0] rounded" />
              )}
              {category.label}
            </button>
          ))}
        </div>

        {/* Template Options */}
        <div className="w-56 shrink-0 bg-[#F5F5F5] p-5 overflow-y-auto">
          <div className="space-y-3">
            {getCategoryTemplates(selectedCategory).map((template) => (
              <div
                key={template.id}
                draggable
                onDragStart={() => handleDragStart(template)}
                className="cursor-move"
              >
                <Button
                  type="outline"
                  onClick={() => addElementToCanvas(template)}
                  className="text-black border-gray-700/15 w-full"
                  enableHover={false}
                >
                  {template.label}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Canvas Area */}
        <div
          className="flex-1 flex flex-col bg-[#F5F5F5] overflow-y-auto"
          onClick={() => {
            setSelectedElement(null);
            closeFloatingMenu();
          }}
        >
          {/* Canvas */}
          <div className="flex items-start justify-center p-8">
            {/* Outer box reserves the on-screen (scaled) footprint so the
                surrounding layout doesn't overlap the scaled-down canvas. */}
            <div
              className="relative"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: canvasDimensions.width * canvasScale,
                height: canvasDimensions.height * canvasScale,
                // Room for the phone hardware drawn outside the screen edge.
                margin: canvasDimensions.isPhone
                  ? PHONE_FRAME_REACH * canvasScale
                  : undefined,
              }}
            >
              {/* Canvas Container — rendered at true interface dimensions and
                  scaled to fit, mirroring how the preview fits its canvas. */}
              <div
                ref={canvasRef}
                className={`relative bg-white ${canvasDimensions.rounded} ${
                  // An inset border would shrink the phone screen below its
                  // true width; the frame marks the edge instead.
                  canvasDimensions.isPhone ? "" : "border-[3px] border-[#EA33C0]"
                }`}
                style={{
                  width: `${canvasDimensions.width}px`,
                  height: `${canvasDimensions.height}px`,
                  transform: `scale(${canvasScale})`,
                  transformOrigin: "top left",
                }}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedElement(null);
                    closeFloatingMenu();
                  }
                }}
              >
                {/* Clips content pushed past the screen by an expanded "… more"
                    text. Pointer-transparent itself so clicks on empty canvas
                    still reach the canvas (deselect, drop). */}
                {(() => {
                const renderElement = (element: any) =>
                    element.visible !== false && (
                      <CanvasElement
                        key={element.id}
                        element={element}
                        shift={textLayout.shifts[element.id]}
                        textExpanded={Boolean(expandedTexts[element.id])}
                        onToggleTextExpanded={() =>
                          setExpandedTexts((prev) => ({
                            ...prev,
                            [element.id]: !prev[element.id],
                          }))
                        }
                        onMeasureText={(extra) => measureText(element.id, extra)}
                        isSelected={element.id === selectedElement}
                        onSelect={() => setSelectedElement(element.id)}
                        canvasWidth={canvasDimensions.width}
                        canvasHeight={canvasDimensions.height}
                        canvasScale={canvasScale}
                        siblingBoxes={alignmentBoxes}
                        onGuidesChange={setActiveGuides}
                        fieldLabel={getFieldLabel(
                          getSubComponent(element.category, element.templateId),
                          element.label,
                        )}
                        uploads={mediaUploads
                          .filter((u) => u.elementId === element.id)
                          .sort(
                            (a, b) => (a.slotIndex ?? 0) - (b.slotIndex ?? 0),
                          )}
                        onOpenTextSettings={(x, y) =>
                          openFloatingMenu(
                            "text-settings",
                            element.id,
                            x * canvasScale,
                            y * canvasScale,
                          )
                        }
                        onOpenProperties={(x, y) =>
                          openFloatingMenu(
                            "properties",
                            element.id,
                            x * canvasScale,
                            y * canvasScale,
                          )
                        }
                        onOpenShapeProperties={(x, y) =>
                          openFloatingMenu(
                            "shape-properties",
                            element.id,
                            x * canvasScale,
                            y * canvasScale,
                          )
                        }
                        onOpenChromeProperties={(x, y) =>
                          openFloatingMenu(
                            "chrome-properties",
                            element.id,
                            x * canvasScale,
                            y * canvasScale,
                          )
                        }
                        onUpdatePosition={(x, y) => {
                          updateElementMultipleProperties(element.id, {
                            x,
                            y,
                          });
                        }}
                        onUpdateSize={(width, height) => {
                          updateElementMultipleProperties(element.id, {
                            width,
                            height,
                          });
                        }}
                        onDelete={() => removeCanvasElement(element.id)}
                      />
                    );

                const isPinned = (element: any) => Boolean(element.properties?.pinned);
                const scrolling = textLayout.totalExtra > 0;

                return (
                  <>
                    {/* Content layer. Once an expanded "… more" text makes the
                        screen longer it scrolls, like the real feed; until then
                        it does not clip, so handles near the edge stay usable.
                        Pointer-transparent itself so clicks on empty canvas
                        still reach the canvas (deselect, drop). */}
                    <div
                      className={`absolute inset-0 pointer-events-none ${
                        scrolling
                          ? "overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                          : ""
                      }`}
                    >
                      <div
                        className="relative w-full pointer-events-none"
                        style={{
                          height: canvasDimensions.height + textLayout.totalExtra,
                        }}
                      >
                        {canvasElements.filter((el) => !isPinned(el)).map(renderElement)}
                      </div>
                    </div>
                    {/* "Fixed on screen" elements stay put above the content. */}
                    {canvasElements.filter(isPinned).map(renderElement)}
                  </>
                );
                })()}

                {/* Alignment guides. Drawn above the elements but inside the
                    canvas coordinate space, so their positions need no scaling.
                    Widths are divided by the scale to stay 1px on screen at any
                    zoom. */}
                {activeGuides.map((guide, i) => (
                  <div
                    key={`${guide.orientation}-${guide.position}-${i}`}
                    style={
                      guide.orientation === "v"
                        ? {
                            position: "absolute",
                            left: guide.position,
                            top: guide.start,
                            height: guide.end - guide.start,
                            width: Math.max(1 / canvasScale, 0.5),
                            backgroundColor: "#EA33C0",
                            pointerEvents: "none",
                            zIndex: 9998,
                          }
                        : {
                            position: "absolute",
                            top: guide.position,
                            left: guide.start,
                            width: guide.end - guide.start,
                            height: Math.max(1 / canvasScale, 0.5),
                            backgroundColor: "#EA33C0",
                            pointerEvents: "none",
                            zIndex: 9998,
                          }
                    }
                  />
                ))}

                {canvasElements.length === 0 && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <ImageIcon size={80} className="text-gray-400 mb-3" />
                    <p className="text-gray-400 text-sm">
                      Drag and drop elements here
                    </p>
                  </div>
                )}

                {/* Above unselected elements so it rounds their corners, but
                    below the selected one (z 20) so its handles stay usable. */}
                {canvasDimensions.isPhone && (
                  <PhoneFrame
                    width={canvasDimensions.width}
                    height={canvasDimensions.height}
                    className="z-[15]"
                  />
                )}
              </div>

              {/* Floating Menus — rendered outside the scaled canvas, so their
                  coordinates are already converted to scaled screen space. */}
              {floatingMenu &&
                floatingMenu.type === "text-settings" &&
                selectedElementData && (
                  <FloatingTextSettings
                    element={selectedElementData}
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `textSettings.${key}`,
                        value,
                      )
                    }
                    onUpdateContent={(value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        "content",
                        value,
                      )
                    }
                  />
                )}

              {floatingMenu &&
                floatingMenu.type === "properties" &&
                selectedElementData && (
                  <FloatingProperties
                    element={selectedElementData}
                    defaultCharacterLimit={
                      getSubComponent(
                        selectedElementData.category,
                        selectedElementData.templateId,
                      )?.settings?.character_limit
                    }
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `properties.${key}`,
                        value,
                      )
                    }
                    onUpdateLabel={(value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        "label",
                        value,
                      )
                    }
                    fieldLabel={getFieldLabel(
                      getSubComponent(
                        selectedElementData.category,
                        selectedElementData.templateId,
                      ),
                      selectedElementData.layerName,
                    )}
                  />
                )}

              {floatingMenu &&
                floatingMenu.type === "chrome-properties" &&
                selectedElementData && (
                  <FloatingChromeProperties
                    element={selectedElementData}
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) => {
                      // Each category keeps its own settings bag, so the panel
                      // writes into the one matching the selected element.
                      const bag =
                        selectedElementData.category === "icon"
                          ? "iconSettings"
                          : selectedElementData.category === "avatar"
                            ? "avatarSettings"
                            : "gradientSettings";
                      updateElementProperty(
                        selectedElementData.id,
                        `${bag}.${key}`,
                        value,
                      );
                    }}
                    onUpdateProperty={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `properties.${key}`,
                        value,
                      )
                    }
                  />
                )}

              {floatingMenu &&
                floatingMenu.type === "shape-properties" &&
                selectedElementData && (
                  <FloatingShapeProperties
                    element={selectedElementData}
                    position={{ x: floatingMenu.x, y: floatingMenu.y }}
                    onClose={closeFloatingMenu}
                    onUpdate={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `shapeSettings.${key}`,
                        value,
                      )
                    }
                    onUpdateProperty={(key, value) =>
                      updateElementProperty(
                        selectedElementData.id,
                        `properties.${key}`,
                        value,
                      )
                    }
                  />
                )}
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Bottom Content Form */}
          <div className="p-10 w-full">
            {/* Objectives Section */}
            {(() => {
              const objectiveSubs = getSubComponentsByCategory("objective");
              if (objectiveSubs.length === 0) return null;
              return (
                <div className="mb-10">
                  <h2 className="text-2xl font-semibold text-black mb-2">
                    Objectives
                  </h2>
                  <hr className="border-gray-200 mb-4" />
                  <div className="grid grid-cols-4 gap-6">
                    {objectiveSubs.map(({ sub, element }) => (
                      <div key={element.id}>
                        <label className="block text-sm font-semibold mb-3 text-black">
                          {getFieldLabel(sub)}
                        </label>
                        {sub.settings.input_type === "select" &&
                        sub.settings.options ? (
                          <Dropdown
                            selected={
                              formValues[element.id] ||
                              sub.settings.options[0]?.value ||
                              ""
                            }
                            setSelected={(val: string) =>
                              updateFormValue(sub.key, val, element.id)
                            }
                            options={sub.settings.options}
                            className="w-full"
                          />
                        ) : (
                          <TextField
                            type="text"
                            value={element.content ?? formValues[element.id] ?? ""}
                            onChange={(e) =>
                              updateFormValue(sub.key, e.target.value, element.id)
                            }
                            placeholder={
                              sub.settings.placeholder || "Enter value..."
                            }
                            className="w-full"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Content Section */}
            {(() => {
              const contentSubs = getSubComponentsByCategory("content");
              if (contentSubs.length === 0) return null;
              return (
                <div className="mb-10">
                  <h2 className="text-2xl font-semibold text-black mb-2">
                    Content
                  </h2>
                  <hr className="border-gray-200 mb-4" />
                  <div className="grid grid-cols-2 gap-8">
                    {contentSubs.map(({ sub, element }) => {
                      // The element's content is the single source of truth, so
                      // this field and the canvas always show the same string.
                      const value =
                        element.content ?? formValues[element.id] ?? "";
                      // This template's own limits win over the catalogue's.
                      const charLimit =
                        element.properties?.characterLimit ??
                        sub.settings.character_limit;
                      const softLimit = element.properties?.softLimit;
                      return (
                        <div key={element.id}>
                          <label className="block text-sm font-semibold mb-3 text-black">
                            {getFieldLabel(sub)}
                          </label>
                          {sub.settings.input_type === "textarea" ? (
                            <>
                              <TextareaField
                                value={value}
                                onChange={(e) =>
                                  updateFormValue(
                                    sub.key,
                                    e.target.value,
                                    element.id,
                                  )
                                }
                                placeholder={
                                  sub.settings.placeholder ||
                                  "Placeholder text...."
                                }
                                rows={sub.settings.rows || 4}
                                maxLength={charLimit}
                                className="w-full"
                              />
                              <CharacterCounter
                                length={value.length}
                                limit={charLimit}
                                softLimit={softLimit}
                                className="mt-2"
                              />
                            </>
                          ) : (
                            <>
                              <TextField
                                type="text"
                                value={value}
                                onChange={(e) =>
                                  updateFormValue(
                                    sub.key,
                                    e.target.value,
                                    element.id,
                                  )
                                }
                                placeholder={
                                  sub.settings.placeholder ||
                                  "Placeholder text...."
                                }
                                maxLength={charLimit}
                                className="w-full"
                              />
                              <CharacterCounter
                                length={value.length}
                                limit={charLimit}
                                softLimit={softLimit}
                                className="mt-2"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Media Section */}
            <div>
              <h3 className="text-xl font-semibold text-black mb-2">Media</h3>
              <hr className="border-gray-200 mb-4" />

              {(() => {
                const { imageUploads, videoUploads, audioUploads, previewUploads } =
                  getMediaUploadsByCategory();
                const hasMedia =
                  imageUploads.length > 0 ||
                  videoUploads.length > 0 ||
                  audioUploads.length > 0 ||
                  previewUploads.length > 0;

                if (!hasMedia) {
                  return (
                    <p className="text-sm text-gray-400 italic">
                      No media elements added yet. Add Image, Video, Audio, or
                      Preview elements from the sidebar.
                    </p>
                  );
                }

                // Helper: remove-element + remove-file buttons shared across types
                const RemoveElementBtn = ({ elementId, label }: { elementId: string; label?: string }) => (
                  <button
                    onClick={() => removeCanvasElement(elementId)}
                    className="absolute -top-2.5 -right-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50 z-10 shadow-sm"
                    title={`Remove ${label || "element"}`}
                  >
                    <X size={14} className="text-gray-600 hover:text-red-500" />
                  </button>
                );

                // Helper: group uploads by elementId so each canvas element renders as one block
                const groupByElement = (uploads: MediaUpload[]) => {
                  const map = new Map<string, MediaUpload[]>();
                  uploads.forEach((u) => {
                    if (!map.has(u.elementId)) map.set(u.elementId, []);
                    map.get(u.elementId)!.push(u);
                  });
                  return Array.from(map.entries());
                };

                return (
                  <>
                    {/* ── Image uploads ─────────────────────────────── */}
                    {imageUploads.length > 0 && (
                      <div className="mb-8">
                        <p className="text-sm font-semibold text-gray-700 mb-4">
                          Images
                        </p>
                        <div className="space-y-6">
                          {groupByElement(imageUploads).map(([elementId, uploads]) => {
                            const element = canvasElements.find((el) => el.id === elementId);
                            const isCarousel = element?.templateId === "carousel";
                            return (
                              <div key={elementId}>
                                <div className="flex items-center gap-2 mb-3">
                                  <span className="text-xs font-medium text-gray-600">
                                    {element?.layerName || "Image"}
                                  </span>
                                  {isCarousel && (
                                    <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                      Carousel · {uploads.length} cards
                                    </span>
                                  )}
                                </div>
                                {/* Carousel: horizontal scrollable card strip */}
                                <div className={`flex gap-3 ${isCarousel ? "overflow-x-auto pb-2" : "flex-wrap"}`}>
                                  {uploads.map((upload, idx) => (
                                    <div key={upload.id} className="relative flex-shrink-0">
                                      {isCarousel && (
                                        <div className="text-[10px] text-gray-500 mb-1 text-center font-medium">
                                          Card {idx + 1}
                                        </div>
                                      )}
                                      {!isCarousel && (
                                        <div className="absolute -top-2.5 -left-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-xs font-semibold z-10 shadow-sm">
                                          {idx + 1}
                                        </div>
                                      )}
                                      <RemoveElementBtn elementId={elementId} label={element?.label} />
                                      {upload.preview ? (
                                        <div className="relative w-28 h-28 border-2 border-gray-300 rounded-lg overflow-hidden group">
                                          <img
                                            src={upload.preview}
                                            alt={`Card ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                          <button
                                            onClick={() => removeMediaUpload(upload.id)}
                                            className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                          >
                                            <X size={12} className="text-red-500" />
                                          </button>
                                        </div>
                                      ) : (
                                        <label className="w-28 h-28 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA33C0] hover:bg-purple-50 transition-colors bg-white">
                                          <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleMediaUpload(upload.id, e)}
                                            className="hidden"
                                          />
                                          <div className="text-3xl text-gray-300 font-light mb-1">+</div>
                                          <p className="text-[10px] text-gray-400 text-center px-1">
                                            Image (30MB)
                                          </p>
                                        </label>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ── Video uploads ─────────────────────────────── */}
                    {videoUploads.length > 0 && (
                      <div className="mb-8">
                        <p className="text-sm font-semibold text-gray-700 mb-4">
                          Videos
                        </p>
                        <div className="space-y-6">
                          {groupByElement(videoUploads).map(([elementId, uploads]) => {
                            const element = canvasElements.find((el) => el.id === elementId);
                            const isCarousel = element?.templateId === "carousel";
                            return (
                              <div key={elementId}>
                                <div className="flex items-center gap-2 mb-3">
                                  <span className="text-xs font-medium text-gray-600">
                                    {element?.layerName || "Video"}
                                  </span>
                                  {isCarousel && (
                                    <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                      Carousel · {uploads.length} clips
                                    </span>
                                  )}
                                </div>
                                <div className={`flex gap-3 ${isCarousel ? "overflow-x-auto pb-2" : "flex-wrap"}`}>
                                  {uploads.map((upload, idx) => (
                                    <div key={upload.id} className="relative flex-shrink-0">
                                      {isCarousel && (
                                        <div className="text-[10px] text-gray-500 mb-1 text-center font-medium">
                                          Clip {idx + 1}
                                        </div>
                                      )}
                                      {!isCarousel && (
                                        <div className="absolute -top-2.5 -left-2.5 w-6 h-6 bg-white border-2 border-gray-300 rounded-full flex items-center justify-center text-xs font-semibold z-10 shadow-sm">
                                          {idx + 1}
                                        </div>
                                      )}
                                      <RemoveElementBtn elementId={elementId} label={element?.label} />
                                      {upload.preview ? (
                                        <div className="relative w-28 h-28 border-2 border-gray-300 rounded-lg overflow-hidden group">
                                          <video
                                            src={upload.preview}
                                            className="w-full h-full object-cover"
                                            muted
                                          />
                                          <button
                                            onClick={() => removeMediaUpload(upload.id)}
                                            className="absolute top-1.5 right-1.5 w-5 h-5 bg-white rounded-full flex items-center justify-center hover:bg-red-50 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                          >
                                            <X size={12} className="text-red-500" />
                                          </button>
                                        </div>
                                      ) : (
                                        <label className="w-28 h-28 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#EA33C0] hover:bg-purple-50 transition-colors bg-white">
                                          <input
                                            type="file"
                                            accept="video/*"
                                            onChange={(e) => handleMediaUpload(upload.id, e)}
                                            className="hidden"
                                          />
                                          <div className="text-3xl text-gray-300 font-light mb-1">+</div>
                                          <p className="text-[10px] text-gray-400 text-center px-1">
                                            Video (30MB)
                                          </p>
                                        </label>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ── Audio uploads ─────────────────────────────── */}
                    {audioUploads.length > 0 && (
                      <div className="mb-8">
                        <p className="text-sm font-semibold text-gray-700 mb-4">
                          Audio
                        </p>
                        <div className="space-y-4">
                          {groupByElement(audioUploads).map(([elementId, uploads]) => {
                            const element = canvasElements.find((el) => el.id === elementId);
                            const isPlaylist = element?.templateId === "audio_playlist";
                            return (
                              <div key={elementId} className="border border-gray-200 rounded-lg overflow-hidden">
                                {/* Element header */}
                                <div className="flex items-center justify-between px-4 py-2 bg-purple-50 border-b border-purple-100">
                                  <div className="flex items-center gap-2">
                                    <Music size={14} className="text-purple-500" />
                                    <span className="text-xs font-medium text-gray-700">
                                      {element?.layerName || "Audio"}
                                    </span>
                                    {isPlaylist && (
                                      <span className="text-[10px] bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                                        Playlist · {uploads.length} tracks
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => removeCanvasElement(elementId)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                    title="Remove element"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                {/* Track rows */}
                                <div className="divide-y divide-gray-100">
                                  {uploads.map((upload, idx) => (
                                    <div
                                      key={upload.id}
                                      className="flex items-center gap-3 px-4 py-3"
                                    >
                                      <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 text-[10px] font-semibold text-purple-600">
                                        {idx + 1}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        {upload.preview ? (
                                          <div className="flex items-center gap-2">
                                            <div className="flex-1 h-1.5 bg-purple-100 rounded-full">
                                              <div className="h-full w-0 bg-purple-400 rounded-full" />
                                            </div>
                                            <span className="text-[10px] text-gray-500 truncate max-w-[120px]">
                                              {upload.file?.name || "Uploaded"}
                                            </span>
                                          </div>
                                        ) : (
                                          <span className="text-[11px] text-gray-400">
                                            {isPlaylist ? `Track ${idx + 1}` : "Audio file"} — not uploaded
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <label className="cursor-pointer text-purple-400 hover:text-purple-600 transition-colors">
                                          <Music size={14} />
                                          <input
                                            type="file"
                                            accept="audio/*,.mp3,.wav,.ogg,.aac,.m4a"
                                            onChange={(e) => handleMediaUpload(upload.id, e)}
                                            className="hidden"
                                          />
                                        </label>
                                        {upload.preview && (
                                          <button
                                            onClick={() => removeMediaUpload(upload.id)}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                          >
                                            <X size={13} />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ── Document / PDF uploads ────────────────────── */}
                    {previewUploads.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-4">
                          Documents
                        </p>
                        <div className="space-y-3">
                          {groupByElement(previewUploads).map(([elementId, uploads]) => {
                            const element = canvasElements.find((el) => el.id === elementId);
                            const isPdf = element?.templateId === "pdf_preview";
                            return (
                              <div
                                key={elementId}
                                className="border border-amber-200 rounded-lg overflow-hidden"
                              >
                                <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-100">
                                  <div className="flex items-center gap-2">
                                    <FileText size={14} className="text-amber-500" />
                                    <span className="text-xs font-medium text-gray-700">
                                      {element?.layerName || "Document"}
                                    </span>
                                    <span className="text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">
                                      {isPdf ? "PDF" : "Document"}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => removeCanvasElement(elementId)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                {uploads.map((upload, idx) => (
                                  <div
                                    key={upload.id}
                                    className="flex items-center gap-3 px-4 py-3"
                                  >
                                    <FileText size={18} className="text-amber-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      {upload.preview ? (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-700 font-medium truncate">
                                            {upload.file?.name || "Uploaded"}
                                          </span>
                                          {isPdf && (
                                            <a
                                              href={upload.preview}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-[10px] text-blue-500 hover:underline flex-shrink-0"
                                            >
                                              Preview
                                            </a>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-[11px] text-gray-400">
                                          {isPdf ? "PDF file" : `Document ${idx + 1}`} — not uploaded
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <label className="cursor-pointer text-amber-400 hover:text-amber-600 transition-colors">
                                        <FileText size={14} />
                                        <input
                                          type="file"
                                          accept={
                                            isPdf
                                              ? ".pdf"
                                              : ".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md"
                                          }
                                          onChange={(e) => handleMediaUpload(upload.id, e)}
                                          className="hidden"
                                        />
                                      </label>
                                      {upload.preview && (
                                        <button
                                          onClick={() => removeMediaUpload(upload.id)}
                                          className="text-gray-400 hover:text-red-500 transition-colors"
                                        >
                                          <X size={13} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Layers Panel */}
        <div
          className={`bg-[#dedede72] border-l border-gray-300 flex flex-col overflow-hidden transition-all duration-300 ${
            showLayersPanel ? "w-56" : "w-10"
          }`}
        >
          <div className="px-2 py-3 border-b border-gray-300 flex items-center justify-between gap-2">
            {showLayersPanel && (
              <h3 className="text-sm font-semibold text-black px-2">Layers</h3>
            )}
            <button
              onClick={() => setShowLayersPanel(!showLayersPanel)}
              className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-gray-300/60 text-gray-600 hover:text-[#EA33C0] transition-colors"
              title={showLayersPanel ? "Hide layers" : "Show layers"}
            >
              {showLayersPanel ? (
                <ChevronRight size={16} />
              ) : (
                <Layers size={16} />
              )}
            </button>
          </div>
          {showLayersPanel && (
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {canvasElements
                .slice()
                .reverse()
                .map((element, reversedIndex) => {
                  // Map reversed index back to original array index
                  const originalIndex =
                    canvasElements.length - 1 - reversedIndex;
                  return (
                    <LayerItem
                      key={element.id}
                      element={element}
                      isSelected={element.id === selectedElement}
                      onSelect={() => setSelectedElement(element.id)}
                      onToggleVisibility={() =>
                        toggleLayerVisibility(element.id)
                      }
                      onRename={(name) => renameLayer(element.id, name)}
                      index={originalIndex}
                      isDragOver={dragOverLayerIndex === originalIndex}
                      onLayerDragStart={() =>
                        setDraggedLayerIndex(originalIndex)
                      }
                      onLayerDragOver={(e: React.DragEvent) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        setDragOverLayerIndex(originalIndex);
                      }}
                      onLayerDrop={() => {
                        if (
                          draggedLayerIndex !== null &&
                          draggedLayerIndex !== originalIndex
                        ) {
                          reorderLayers(draggedLayerIndex, originalIndex);
                        }
                        setDraggedLayerIndex(null);
                        setDragOverLayerIndex(null);
                      }}
                      onLayerDragEnd={() => {
                        setDraggedLayerIndex(null);
                        setDragOverLayerIndex(null);
                      }}
                    />
                  );
                })}
              {canvasElements.length === 0 && (
                <p className="text-xs text-gray-400 italic p-2">
                  No layers yet
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Canvas Element Component
/**
 * Carousel arrows, dots and counter badge.
 * Mirrors the preview renderer in ad-builder-canvas.tsx so a carousel looks the
 * same while designing as it does in the campaign preview.
 */
const CarouselNav: React.FC<{
  slideIdx: number;
  totalSlides: number;
  onChange: (idx: number) => void;
}> = ({ slideIdx, totalSlides, onChange }) => (
  <>
    <button
      className="absolute left-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white hover:bg-black/60 pointer-events-auto"
      style={{ zIndex: 10 }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onChange((slideIdx - 1 + totalSlides) % totalSlides);
      }}
    >
      <ChevronLeft size={12} />
    </button>
    <button
      className="absolute right-1 top-1/2 -translate-y-1/2 w-5 h-5 bg-black/40 rounded-full flex items-center justify-center text-white hover:bg-black/60 pointer-events-auto"
      style={{ zIndex: 10 }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onChange((slideIdx + 1) % totalSlides);
      }}
    >
      <ChevronRight size={12} />
    </button>
    <div
      className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1"
      style={{ zIndex: 10 }}
    >
      {Array.from({ length: totalSlides }).map((_, i) => (
        <button
          key={i}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onChange(i);
          }}
          className="w-1.5 h-1.5 rounded-full transition-colors pointer-events-auto"
          style={{
            backgroundColor:
              i === slideIdx ? "white" : "rgba(255,255,255,0.4)",
          }}
        />
      ))}
    </div>
    <div
      className="absolute top-1 right-1 bg-black/50 text-white text-[9px] px-1.5 py-0.5 rounded-full"
      style={{ zIndex: 10 }}
    >
      {slideIdx + 1}/{totalSlides}
    </div>
  </>
);

interface CanvasElementProps {
  element: any;
  /** Display-only push from an expanded "… more" text above this element. */
  shift?: { dy: number; dh: number };
  textExpanded?: boolean;
  onToggleTextExpanded?: () => void;
  onMeasureText?: (extraHeight: number) => void;
  isSelected: boolean;
  onSelect: () => void;
  onOpenTextSettings: (x: number, y: number) => void;
  onOpenProperties: (x: number, y: number) => void;
  onOpenShapeProperties: (x: number, y: number) => void;
  onOpenChromeProperties: (x: number, y: number) => void;
  onUpdatePosition: (x: number, y: number) => void;
  onUpdateSize: (width: number, height: number) => void;
  onDelete: () => void;
  canvasWidth: number;
  canvasHeight: number;
  /** Uploaded media for this element, ordered by slot, so the canvas is WYSIWYG. */
  uploads: MediaUpload[];
  /** Canvas fit-scale, used to convert screen deltas into canvas coordinates. */
  canvasScale: number;
  /** Business-facing label, shared with the form and the Properties panel. */
  fieldLabel: string;
  /** Other visible elements this one can snap to while being dragged. */
  siblingBoxes?: Box[];
  /** Reports the guides to draw during a drag; called with [] on release. */
  onGuidesChange?: (guides: Guide[]) => void;
}

const CanvasElement: React.FC<CanvasElementProps> = ({
  element,
  shift,
  textExpanded = false,
  onToggleTextExpanded,
  onMeasureText,
  isSelected,
  onSelect,
  onOpenTextSettings,
  onOpenProperties,
  onOpenShapeProperties,
  onOpenChromeProperties,
  onUpdatePosition,
  onUpdateSize,
  onDelete,
  canvasWidth,
  canvasHeight,
  uploads,
  canvasScale,
  fieldLabel,
  siblingBoxes,
  onGuidesChange,
}) => {
  // Which carousel/playlist slot is showing in the canvas preview
  const [slideIdx, setSlideIdx] = useState(0);
  const isDraggingRef = useRef(false);
  const isResizingRef = useRef(false);
  const resizeDirectionRef = useRef("");
  const dragStart = useRef<{
    x: number;
    y: number;
    elemX: number;
    elemY: number;
    elemWidth: number;
    elemHeight: number;
  }>({ x: 0, y: 0, elemX: 0, elemY: 0, elemWidth: 0, elemHeight: 0 });

  // Keep stable references to the latest callbacks
  const onUpdatePositionRef = useRef(onUpdatePosition);
  const onUpdateSizeRef = useRef(onUpdateSize);
  const elementRef = useRef(element);
  const canvasScaleRef = useRef(canvasScale);
  // Alignment targets and the guide callback, held as refs for the same reason
  // as the others: the mousemove listener is bound once and must not close over
  // stale values.
  const siblingBoxesRef = useRef(siblingBoxes ?? []);
  const onGuidesChangeRef = useRef(onGuidesChange);
  onUpdatePositionRef.current = onUpdatePosition;
  onUpdateSizeRef.current = onUpdateSize;
  elementRef.current = element;
  canvasScaleRef.current = canvasScale;
  siblingBoxesRef.current = siblingBoxes ?? [];
  onGuidesChangeRef.current = onGuidesChange;

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Check if clicking on resize handle
    const direction = target.getAttribute("data-direction");
    if (direction) {
      e.stopPropagation();
      e.preventDefault();
      onSelect();
      isResizingRef.current = true;
      resizeDirectionRef.current = direction;

      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        elemX: element.x,
        elemY: element.y,
        elemWidth: element.width,
        elemHeight: element.height,
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return;
    }

    // Regular drag
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    isDraggingRef.current = true;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      elemX: element.x,
      elemY: element.y,
      elemWidth: element.width,
      elemHeight: element.height,
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDraggingRef.current) {
      // Mouse deltas are in screen px; the canvas may be scaled down to fit, so
      // convert them into canvas coordinates or the element outruns the cursor.
      const scale = canvasScaleRef.current || 1;
      const dx = (e.clientX - dragStart.current.x) / scale;
      const dy = (e.clientY - dragStart.current.y) / scale;

      // Calculate new position
      let newX = dragStart.current.elemX + dx;
      let newY = dragStart.current.elemY + dy;

      // Constrain to canvas bounds
      const elWidth = elementRef.current.width;
      const elHeight = elementRef.current.height;
      newX = Math.max(0, Math.min(newX, canvasWidth - elWidth));
      newY = Math.max(0, Math.min(newY, canvasHeight - elHeight));

      // Snap to neighbouring elements and report the guides to draw. Applied
      // after the bounds clamp so a snap can never push the element off-canvas.
      const snap = computeSnap(
        {
          id: elementRef.current.id,
          x: newX,
          y: newY,
          width: elWidth,
          height: elHeight,
        },
        siblingBoxesRef.current,
      );
      newX = Math.max(0, Math.min(snap.x, canvasWidth - elWidth));
      newY = Math.max(0, Math.min(snap.y, canvasHeight - elHeight));
      onGuidesChangeRef.current?.(snap.guides);

      onUpdatePositionRef.current(newX, newY);
    } else if (isResizingRef.current) {
      const scale = canvasScaleRef.current || 1;
      const dx = (e.clientX - dragStart.current.x) / scale;
      const dy = (e.clientY - dragStart.current.y) / scale;
      const direction = resizeDirectionRef.current;

      let newX = dragStart.current.elemX;
      let newY = dragStart.current.elemY;
      let newWidth = dragStart.current.elemWidth;
      let newHeight = dragStart.current.elemHeight;

      // Calculate new dimensions based on resize direction
      switch (direction) {
        case "se": // Southeast - bottom right
          newWidth = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemWidth + dx);
          newHeight = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemHeight + dy);
          break;
        case "nw": // Northwest - top left
          newWidth = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemWidth - dx);
          newHeight = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemHeight - dy);
          newX =
            dragStart.current.elemX + (dragStart.current.elemWidth - newWidth);
          newY =
            dragStart.current.elemY +
            (dragStart.current.elemHeight - newHeight);
          break;
        case "ne": // Northeast - top right
          newWidth = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemWidth + dx);
          newHeight = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemHeight - dy);
          newY =
            dragStart.current.elemY +
            (dragStart.current.elemHeight - newHeight);
          break;
        case "sw": // Southwest - bottom left
          newWidth = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemWidth - dx);
          newHeight = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemHeight + dy);
          newX =
            dragStart.current.elemX + (dragStart.current.elemWidth - newWidth);
          break;
        case "n": // North - top
          newHeight = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemHeight - dy);
          newY =
            dragStart.current.elemY +
            (dragStart.current.elemHeight - newHeight);
          break;
        case "s": // South - bottom
          newHeight = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemHeight + dy);
          break;
        case "e": // East - right
          newWidth = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemWidth + dx);
          break;
        case "w": // West - left
          newWidth = Math.max(MIN_ELEMENT_SIZE, dragStart.current.elemWidth - dx);
          newX =
            dragStart.current.elemX + (dragStart.current.elemWidth - newWidth);
          break;
      }

      // Constrain to canvas bounds
      if (newX < 0) {
        newWidth += newX;
        newX = 0;
      }
      if (newY < 0) {
        newHeight += newY;
        newY = 0;
      }
      if (newX + newWidth > canvasWidth) {
        newWidth = canvasWidth - newX;
      }
      if (newY + newHeight > canvasHeight) {
        newHeight = canvasHeight - newY;
      }

      onUpdatePositionRef.current(newX, newY);
      onUpdateSizeRef.current(newWidth, newHeight);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    isResizingRef.current = false;
    resizeDirectionRef.current = "";
    // Guides are a drag-time affordance only, so they clear on release.
    onGuidesChangeRef.current?.([]);
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Check if this is a Safe Area element
  const isSafeArea = element.templateId === "safe_area";

  return (
    <div
      className={`absolute select-none group ${
        isSafeArea ? "pointer-events-none" : "pointer-events-auto cursor-move"
      }`}
      style={{
        left: element.x,
        top: element.y + (shift?.dy ?? 0),
        width: element.width,
        height: element.height + (shift?.dh ?? 0),
        // Hidden elements stay visible-but-dimmed here so they remain
        // selectable while designing; the preview omits them entirely.
        // "Show in template only" elements are part of the design but not the
        // rendered ad, so they are dimmed here and omitted from the preview.
        // Objectives (CTA, landing page URL) are never drawn in the ad, so
        // they are always "template only" — dimmed less, to stay readable.
        opacity: element.properties?.showInTemplateOnly
          ? element.category === "attributes" && !isSafeArea
            ? 0.8
            : 0.35
          : undefined,
        // Elements are transparent and unpadded so the canvas matches the
        // preview exactly. Selection chrome is drawn with `outline`, which sits
        // outside the box and so never shifts or covers the content.
        backgroundColor: isSafeArea ? "rgba(234,51,192,0.08)" : "transparent",
        border: isSafeArea ? "1.5px dashed #EA33C0" : undefined,
        outline:
          !isSafeArea && isSelected ? "2px solid #EA33C0" : undefined,
        outlineOffset: !isSafeArea && isSelected ? "-1px" : undefined,
        zIndex: isSafeArea ? 1 : isSelected ? 20 : undefined,
      }}
      onMouseDown={isSafeArea ? undefined : handleMouseDown}
    >
      {/* Faint hover outline for unselected elements — hint only, no layout cost */}
      {!isSafeArea && !isSelected && (
        <div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ outline: "1px dashed rgba(234,51,192,0.6)", outlineOffset: "-1px" }}
        />
      )}
      {/* Content */}
      {!isSafeArea && (
        <div className="pointer-events-none w-full h-full overflow-hidden">
          {element.category === "text" && (
              <ExpandableText
                text={element.content ?? ""}
                softLimit={element.properties?.softLimit}
                expanded={textExpanded}
                onToggle={() => onToggleTextExpanded?.()}
                onMeasure={(extra) => onMeasureText?.(extra)}
                boxHeight={element.height}
                placeholder={`${fieldLabel}…`}
                style={{
                  fontSize: element.textSettings?.fontSize ?? 12,
                  fontWeight: element.textSettings?.bold ? "bold" : "normal",
                  fontStyle: element.textSettings?.italic ? "italic" : "normal",
                  textDecoration: [
                    element.textSettings?.underline ? "underline" : "",
                    element.textSettings?.strikethrough ? "line-through" : "",
                  ].filter(Boolean).join(" ") || "none",
                  fontFamily: element.textSettings?.font || "inherit",
                  textAlign: (element.textSettings?.textAlign as any) ?? "left",
                  lineHeight: element.textSettings?.lineHeight ?? 1.4,
                  letterSpacing: element.textSettings?.letterSpacing != null ? `${element.textSettings.letterSpacing}px` : undefined,
                  textTransform: (element.textSettings?.textTransform as any) ?? undefined,
                  opacity: element.textSettings?.opacity != null ? element.textSettings.opacity / 100 : 1,
                  color: element.content
                    ? (element.textSettings?.color ?? "#000000")
                    : "#9ca3af",
                }}
              />
            )}
          {element.category === "image" && (
            (() => {
              const isCarousel = element.templateId === "carousel";
              const totalSlides = isCarousel
                ? Math.max(uploads.length, 1)
                : 1;
              const idx = Math.min(slideIdx, totalSlides - 1);
              const activeUrl = uploads[isCarousel ? idx : 0]?.preview ?? null;

              return (
                <div className="relative w-full h-full">
                  {activeUrl ? (
                    <img
                      src={activeUrl}
                      alt={element.label ?? "image"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                      }}
                      className="border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1"
                    >
                      <svg
                        className="w-7 h-7 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-xs font-medium text-gray-400">
                        {isCarousel
                          ? `Card ${idx + 1} / ${totalSlides}`
                          : (element.label ?? "Image")}
                      </span>
                    </div>
                  )}
                  {isCarousel && totalSlides > 1 && (
                    <CarouselNav
                      slideIdx={idx}
                      totalSlides={totalSlides}
                      onChange={setSlideIdx}
                    />
                  )}
                </div>
              );
            })()
          )}
          {element.category === "video" && (
            (() => {
              const isCarousel = element.templateId === "carousel";
              const totalSlides = isCarousel
                ? Math.max(uploads.length, 1)
                : 1;
              const idx = Math.min(slideIdx, totalSlides - 1);
              const activeUrl = uploads[isCarousel ? idx : 0]?.preview ?? null;

              return (
                <div className="relative w-full h-full">
                  {activeUrl ? (
                    <video
                      src={activeUrl}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      muted
                      loop
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                      }}
                      className="border-2 border-dashed border-gray-600 flex flex-col items-center justify-center gap-1"
                    >
                      <svg
                        className="w-7 h-7 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-xs font-medium text-gray-400">
                        {isCarousel
                          ? `Clip ${idx + 1} / ${totalSlides}`
                          : (element.label ?? "Video")}
                      </span>
                    </div>
                  )}
                  {isCarousel && totalSlides > 1 && (
                    <CarouselNav
                      slideIdx={idx}
                      totalSlides={totalSlides}
                      onChange={setSlideIdx}
                    />
                  )}
                </div>
              );
            })()
          )}
          {element.category === "audio" && (
            (() => {
              const isPlaylist = element.templateId === "audio_playlist";
              const activeUrl = uploads[0]?.preview ?? null;

              if (isPlaylist) {
                return (
                  <div
                    style={{ width: "100%", height: "100%" }}
                    className="bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col p-1 overflow-hidden"
                  >
                    <div className="text-[9px] font-semibold text-purple-500 mb-1 px-1">
                      Playlist
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-0.5">
                      {(uploads.length > 0
                        ? uploads
                        : Array.from({ length: 3 })
                      ).map((slot: any, i: number) => (
                        <div
                          key={i}
                          className={`w-full flex items-center gap-1 px-1 py-0.5 rounded text-left ${
                            i === 0 ? "bg-purple-200" : ""
                          }`}
                        >
                          <Music
                            size={8}
                            className={
                              i === 0 ? "text-purple-600" : "text-gray-400"
                            }
                          />
                          <span className="text-[8px] text-gray-600 truncate">
                            {slot?.file?.name ?? `Track ${i + 1}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  style={{ width: "100%", height: "100%" }}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-dashed border-purple-200 flex flex-col items-center justify-center gap-1"
                >
                  {activeUrl ? (
                    <>
                      <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white">
                        <Music size={14} />
                      </div>
                      <div className="w-4/5 h-1 bg-purple-200 rounded-full">
                        <div className="h-full w-1/3 bg-purple-500 rounded-full" />
                      </div>
                    </>
                  ) : (
                    <>
                      <Music size={20} className="text-purple-400" />
                      <span className="text-[9px] font-medium text-purple-400">
                        {element.label ?? "Audio"}
                      </span>
                      <span className="text-[8px] text-purple-300">
                        Upload audio →
                      </span>
                    </>
                  )}
                </div>
              );
            })()
          )}
          {element.category === "preview" && (
            (() => {
              const isPdf = element.templateId === "pdf_preview";
              const docUrl = uploads[0]?.preview ?? null;

              if (docUrl && isPdf) {
                return (
                  <iframe
                    src={docUrl}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    title="PDF Preview"
                  />
                );
              }

              if (docUrl) {
                return (
                  <div
                    style={{ width: "100%", height: "100%" }}
                    className="bg-blue-50 flex flex-col items-center justify-center gap-1"
                  >
                    <FileText size={24} className="text-blue-500" />
                    <span className="text-[9px] text-blue-600 font-medium text-center px-1 truncate w-full">
                      {uploads[0]?.file?.name ?? "Document"}
                    </span>
                  </div>
                );
              }

              return (
                <div
                  style={{ width: "100%", height: "100%" }}
                  className="bg-amber-50 border-2 border-dashed border-amber-200 flex flex-col items-center justify-center gap-1"
                >
                  <FileText size={20} className="text-amber-400" />
                  <span className="text-[9px] font-medium text-amber-400">
                    {isPdf ? "PDF Preview" : "Document"}
                  </span>
                  <span className="text-[8px] text-amber-300">Upload →</span>
                </div>
              );
            })()
          )}
          {element.category === "icon" && (
            <ChromeIcon
              geometry={element.iconGeometry}
              settings={element.iconSettings}
              label={element.label}
            />
          )}
          {element.category === "avatar" && (
            <ChromeAvatar
              settings={element.avatarSettings}
              url={uploads[0]?.preview ?? null}
              label={element.label}
            />
          )}
          {element.category === "gradient" && (
            <ChromeGradient settings={element.gradientSettings} />
          )}
          {/* Objectives (CTA, landing page URL): a labelled box marking the part
              of the ad the field below the canvas drives. Mirrors the dashed
              safe-area treatment; the rendered ad shows the real button. */}
          {element.category === "attributes" && (
            <div className="relative h-full w-full rounded border-[1.5px] border-dashed border-blue-500 bg-blue-500/10">
              <span className="absolute left-0 top-0 max-w-full truncate rounded-br bg-blue-500 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-white">
                {fieldLabel}
                {element.content ? `: ${element.content}` : ""}
              </span>
            </div>
          )}
          {element.category === "shape" && (
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: element.shapeSettings?.fill ?? "#e5e7eb",
                opacity: element.shapeSettings?.opacity ?? 1,
                borderRadius:
                  element.templateId === "circle"
                    ? "50%"
                    : (element.shapeSettings?.borderRadius ?? 0),
                border: element.shapeSettings?.borderWidth
                  ? `${element.shapeSettings.borderWidth}px solid ${element.shapeSettings.borderColor ?? "transparent"}`
                  : "none",
              }}
            />
          )}
          {element.category === "button" && (
            (() => {
              // Same brand-aware fallbacks the preview applies, so a button with
              // partial settings renders identically in both renderers.
              const brand = BUTTON_BRAND_DEFAULTS[element.templateId] ?? {};
              const bs = element.buttonSettings ?? {};
              const radius =
                bs.borderRadius != null
                  ? typeof bs.borderRadius === "number"
                    ? `${bs.borderRadius}px`
                    : bs.borderRadius
                  : (brand.radius ?? "8px");

              return (
                <div
                  className="flex items-center justify-center gap-2 w-full h-full pointer-events-none"
                  style={{
                    backgroundColor: bs.backgroundColor ?? brand.bg ?? "#EA33C0",
                    color: bs.textColor ?? brand.color ?? "#ffffff",
                    borderRadius: radius,
                    border: bs.borderColor
                      ? `1px solid ${bs.borderColor}`
                      : (brand.border ?? "none"),
                    boxShadow: bs.shadow ?? brand.shadow ?? "none",
                    fontSize: bs.fontSize ?? 14,
                    fontWeight: bs.fontWeight ?? "600",
                    letterSpacing: "0.3px",
                  }}
                >
                  {bs.icon && <span className="text-base">{bs.icon}</span>}
                  <span>{bs.text ?? element.label}</span>
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* Safe Area Label — styling mirrors the preview renderer */}
      {isSafeArea && (
        <div className="w-full h-full flex items-center justify-center pointer-events-none">
          <span className="text-[#EA33C0] text-[9px] font-semibold tracking-wide">
            SAFE AREA
          </span>
        </div>
      )}

      {/* Resize handles */}
      {isSelected && !isSafeArea && (
        <>
          <div
            data-direction="nw"
            className="resize-handle absolute -top-2 -left-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nwse-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="ne"
            className="resize-handle absolute -top-2 -right-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nesw-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="sw"
            className="resize-handle absolute -bottom-2 -left-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nesw-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="se"
            className="resize-handle absolute -bottom-2 -right-2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-nwse-resize border-2 border-white shadow-sm"
          />

          <div
            data-direction="n"
            className="resize-handle absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ns-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="s"
            className="resize-handle absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ns-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="w"
            className="resize-handle absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ew-resize border-2 border-white shadow-sm"
          />
          <div
            data-direction="e"
            className="resize-handle absolute top-1/2 -right-2 -translate-y-1/2 w-4 h-4 bg-[#EA33C0] rounded-full cursor-ew-resize border-2 border-white shadow-sm"
          />
        </>
      )}

      {/* Action Buttons */}
      {isSelected && !isSafeArea && (
        <div className="absolute -bottom-12 left-0 flex gap-2">
          {element.category === "text" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpenTextSettings(
                  Math.min(element.x, 375 - 240),
                  element.y + element.height + 50,
                );
              }}
              className="px-4 py-1.5 bg-white text-[#EA33C0] rounded-full text-xs font-semibold border-2 border-[#EA33C0] hover:bg-pink-50"
            >
              Edit Text
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (element.category === "shape") {
                onOpenShapeProperties(385, element.y);
              } else if (
                element.category === "icon" ||
                element.category === "avatar" ||
                element.category === "gradient"
              ) {
                onOpenChromeProperties(385, element.y);
              } else {
                onOpenProperties(385, element.y);
              }
            }}
            className="px-4 py-1.5 bg-white text-[#EA33C0] rounded-full text-xs font-semibold border-2 border-[#EA33C0] hover:bg-pink-50"
          >
            Properties
          </button>
          {element.category === "button" && (
            <button className="px-4 py-1.5 bg-white text-[#EA33C0] rounded-md text-xs font-semibold border-2 border-[#EA33C0] hover:bg-pink-50">
              CTA
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="px-4 py-1.5 bg-white text-red-500 rounded-full text-xs font-semibold border-2 border-red-500 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

// Floating Text Settings Component
interface FloatingTextSettingsProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (key: string, value: any) => void;
  onUpdateContent: (value: string) => void;
}

const FloatingTextSettings: React.FC<FloatingTextSettingsProps> = ({
  element,
  position,
  onClose,
  onUpdate,
  onUpdateContent,
}) => {
  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">Text Settings</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Font Family */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Font
          </label>
          <div className="relative">
            <select
              value={element.textSettings?.font || ""}
              onChange={(e) => onUpdate("font", e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0] appearance-none bg-white"
            >
              <option value="">Select Option</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Trebuchet MS">Trebuchet MS</option>
              <option value="Courier New">Courier New</option>
            </select>
            <ChevronDown
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#EA33C0] pointer-events-none"
            />
          </div>
        </div>

        {/* Font Size */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Font size (px)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="8"
              max="72"
              value={element.textSettings?.fontSize || 12}
              onChange={(e) => onUpdate("fontSize", parseInt(e.target.value))}
              className="flex-1 accent-[#EA33C0]"
            />
            <input
              type="number"
              value={element.textSettings?.fontSize || 12}
              onChange={(e) => onUpdate("fontSize", parseInt(e.target.value))}
              className="w-14 px-2 py-1.5 border-2 border-[#EA33C0]/40 rounded text-sm text-center focus:outline-none focus:border-[#EA33C0]"
            />
          </div>
        </div>

        {/* Bold / Italic / Underline / Strikethrough */}
        <div className="grid grid-cols-4 gap-1.5">
          <button
            onClick={() => onUpdate("bold", !element.textSettings?.bold)}
            title="Bold"
            className={`py-2 rounded font-bold text-sm transition-colors ${element.textSettings?.bold ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            B
          </button>
          <button
            onClick={() => onUpdate("italic", !element.textSettings?.italic)}
            title="Italic"
            className={`py-2 rounded italic text-sm transition-colors ${element.textSettings?.italic ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            I
          </button>
          <button
            onClick={() => onUpdate("underline", !element.textSettings?.underline)}
            title="Underline"
            className={`py-2 rounded text-sm underline transition-colors ${element.textSettings?.underline ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            U
          </button>
          <button
            onClick={() => onUpdate("strikethrough", !element.textSettings?.strikethrough)}
            title="Strikethrough"
            className={`py-2 rounded text-sm line-through transition-colors ${element.textSettings?.strikethrough ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            S
          </button>
        </div>

        {/* Text Align */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Text Align
          </label>
          <div className="grid grid-cols-4 gap-1.5">
            {(["left", "center", "right", "justify"] as const).map((align) => (
              <button
                key={align}
                onClick={() => onUpdate("textAlign", align)}
                title={align.charAt(0).toUpperCase() + align.slice(1)}
                className={`py-1.5 rounded text-xs transition-colors capitalize ${element.textSettings?.textAlign === align ? "bg-[#EA33C0] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              >
                {align === "left" ? "⫷" : align === "center" ? "≡" : align === "right" ? "⫸" : "⊞"}
              </button>
            ))}
          </div>
        </div>

        {/* Line Height */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Line Height
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0.8"
              max="3"
              step="0.1"
              value={element.textSettings?.lineHeight ?? 1.2}
              onChange={(e) => onUpdate("lineHeight", parseFloat(e.target.value))}
              className="flex-1 accent-[#EA33C0]"
            />
            <input
              type="number"
              min="0.8"
              max="3"
              step="0.1"
              value={element.textSettings?.lineHeight ?? 1.2}
              onChange={(e) => onUpdate("lineHeight", parseFloat(e.target.value))}
              className="w-14 px-2 py-1.5 border-2 border-[#EA33C0]/40 rounded text-sm text-center focus:outline-none focus:border-[#EA33C0]"
            />
          </div>
        </div>

        {/* Letter Spacing */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Letter Spacing (px)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="-2"
              max="10"
              step="0.5"
              value={element.textSettings?.letterSpacing ?? 0}
              onChange={(e) => onUpdate("letterSpacing", parseFloat(e.target.value))}
              className="flex-1 accent-[#EA33C0]"
            />
            <input
              type="number"
              min="-2"
              max="10"
              step="0.5"
              value={element.textSettings?.letterSpacing ?? 0}
              onChange={(e) => onUpdate("letterSpacing", parseFloat(e.target.value))}
              className="w-14 px-2 py-1.5 border-2 border-[#EA33C0]/40 rounded text-sm text-center focus:outline-none focus:border-[#EA33C0]"
            />
          </div>
        </div>

        {/* Text Transform */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Text Transform
          </label>
          <div className="relative">
            <select
              value={element.textSettings?.textTransform || "none"}
              onChange={(e) => onUpdate("textTransform", e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0] appearance-none bg-white"
            >
              <option value="none">None</option>
              <option value="uppercase">UPPERCASE</option>
              <option value="lowercase">lowercase</option>
              <option value="capitalize">Capitalize</option>
            </select>
            <ChevronDown
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#EA33C0] pointer-events-none"
            />
          </div>
        </div>

        {/* Opacity */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Opacity ({element.textSettings?.opacity ?? 100}%)
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={element.textSettings?.opacity ?? 100}
            onChange={(e) => onUpdate("opacity", parseInt(e.target.value))}
            className="w-full accent-[#EA33C0]"
          />
        </div>

        {/* Colour Picker */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Colour Picker
          </label>
          <input
            type="color"
            value={element.textSettings?.color || "#000000"}
            onChange={(e) => onUpdate("color", e.target.value)}
            className="w-full h-10 border-2 border-[#EA33C0]/40 rounded-md cursor-pointer"
          />
        </div>

        {/* Text Content Editor - At Bottom */}
        <div className="border-t border-gray-200 pt-3">
          <textarea
            value={element.content || ""}
            onChange={(e) => onUpdateContent(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0] resize-none"
            rows={3}
            placeholder="Enter text content..."
          />
        </div>
      </div>
    </div>
  );
};

// "Fixed on screen" switch, shared by every properties panel.
const FixedOnScreenToggle: React.FC<{
  pinned: boolean;
  onChange: (value: boolean) => void;
}> = ({ pinned, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <label className="text-sm text-gray-800" title="Stays in place when expanded text pushes the rest of the screen down">
      Fixed on screen
    </label>
    <button
      onClick={() => onChange(!pinned)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        pinned ? "bg-[#EA33C0]" : "bg-gray-300"
      }`}
    >
      <div
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
          pinned ? "translate-x-6" : ""
        }`}
      />
    </button>
  </div>
);

// Character limit (hard) and soft limit for a text element. Empty means "use
// the default"; the soft limit must stay below the character limit.
const CharacterLimitFields: React.FC<{
  characterLimit?: number;
  softLimit?: number;
  defaultLimit?: number;
  onChange: (key: "characterLimit" | "softLimit", value: number | undefined) => void;
}> = ({ characterLimit, softLimit, defaultLimit, onChange }) => {
  const parse = (raw: string) => {
    const value = parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : undefined;
  };
  const effectiveLimit = characterLimit ?? defaultLimit;
  const softTooHigh =
    softLimit !== undefined && effectiveLimit !== undefined && softLimit >= effectiveLimit;
  const inputClass =
    "w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]";

  return (
    <div className="space-y-3 py-2">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Character limit
        </label>
        <input
          type="number"
          min={1}
          value={characterLimit ?? ""}
          placeholder={defaultLimit ? `${defaultLimit} (default)` : "No limit"}
          onChange={(e) => onChange("characterLimit", parse(e.target.value))}
          className={inputClass}
        />
        <p className="mt-1 text-[11px] text-gray-500">Most characters a user can enter.</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Soft limit
        </label>
        <input
          type="number"
          min={1}
          value={softLimit ?? ""}
          placeholder="Off"
          onChange={(e) => onChange("softLimit", parse(e.target.value))}
          className={`${inputClass} ${softTooHigh ? "border-red-400" : ""}`}
        />
        <p className={`mt-1 text-[11px] ${softTooHigh ? "text-red-500" : "text-gray-500"}`}>
          {softTooHigh
            ? `Must be below the character limit (${effectiveLimit}).`
            : 'Text past this is shown as "… more" on the canvas.'}
        </p>
      </div>
    </div>
  );
};

// Floating Properties Component
interface FloatingPropertiesProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (key: string, value: any) => void;
  onUpdateLabel: (value: string) => void;
  /** Business-facing field label, shared with the form below the canvas. */
  fieldLabel: string;
  /** The component catalogue's limit, used when the element sets none. */
  defaultCharacterLimit?: number;
}

const FloatingProperties: React.FC<FloatingPropertiesProps> = ({
  element,
  position,
  onClose,
  onUpdate,
  onUpdateLabel,
  fieldLabel,
  defaultCharacterLimit,
}) => {
  const isMediaElement =
    element.category === "image" || element.category === "video";

  // Only content-bearing elements can be locked: shapes and buttons are
  // template styling and are never user-editable to begin with.
  const supportsEditableToggle = [
    "text",
    "image",
    "video",
    "audio",
    "preview",
  ].includes(element.category);

  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">Properties</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            {fieldLabel}
          </label>
          <input
            type="text"
            value={element.label || ""}
            onChange={(e) => onUpdateLabel(e.target.value)}
            placeholder="Enter Title..."
            className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
          />
        </div>

        {/* Show/hide applies to every element type, media included: it decides
            whether the element is part of the rendered ad or design-only. */}
        <div className="flex items-center justify-between py-2">
          <label className="text-sm text-gray-800">Show in template only</label>
          <button
            onClick={() =>
              onUpdate(
                "showInTemplateOnly",
                !element.properties?.showInTemplateOnly,
              )
            }
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.properties?.showInTemplateOnly
                ? "bg-[#EA33C0]"
                : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.properties?.showInTemplateOnly ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        {/* Fixed on screen: stays put when "… more" text expands and the rest
            of the screen is pushed down (status bar, tab bar, home indicator). */}
        <FixedOnScreenToggle
          pinned={Boolean(element.properties?.pinned)}
          onChange={(value) => onUpdate("pinned", value)}
        />

        {/* Editable: when off, the value authored here is locked on the user
            side — they see it but cannot change it. */}
        {supportsEditableToggle && (
          <div className="flex items-center justify-between py-2">
            <label className="text-sm text-gray-800">Editable</label>
            <button
              onClick={() =>
                onUpdate("editable", element.properties?.editable === false)
              }
              className={`relative w-12 h-6 rounded-full transition-colors ${
                element.properties?.editable !== false
                  ? "bg-[#EA33C0]"
                  : "bg-gray-300"
              }`}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                  element.properties?.editable !== false ? "translate-x-6" : ""
                }`}
              />
            </button>
          </div>
        )}

        {element.category === "text" && (
          <CharacterLimitFields
            characterLimit={element.properties?.characterLimit}
            softLimit={element.properties?.softLimit}
            defaultLimit={defaultCharacterLimit}
            onChange={(key, value) => onUpdate(key, value)}
          />
        )}

        {/* Remaining toggles are not meaningful for media elements */}
        {!isMediaElement && (
          <>
            <div className="flex items-center justify-between py-2">
              <label className="text-sm text-gray-800">Show in Info only</label>
              <button
                onClick={() =>
                  onUpdate(
                    "showInInfoOnly",
                    !element.properties?.showInInfoOnly,
                  )
                }
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  element.properties?.showInInfoOnly
                    ? "bg-[#EA33C0]"
                    : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    element.properties?.showInInfoOnly ? "translate-x-6" : ""
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-2">
              <label className="text-sm text-gray-800">
                Show character count
              </label>
              <button
                onClick={() =>
                  onUpdate(
                    "showCharacterCount",
                    !element.properties?.showCharacterCount,
                  )
                }
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  element.properties?.showCharacterCount
                    ? "bg-[#EA33C0]"
                    : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    element.properties?.showCharacterCount
                      ? "translate-x-6"
                      : ""
                  }`}
                />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Floating Chrome Properties — styling for icon, avatar and gradient elements.
// One panel for all three: they share the show/hide control and differ only in
// which styling block applies, so the element's category picks the block.
interface FloatingChromePropertiesProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  /** Updates a key under the element's own settings bag for its category. */
  onUpdate: (key: string, value: any) => void;
  /** Updates a flag under `properties` (shared with the Properties panel). */
  onUpdateProperty: (key: string, value: any) => void;
}

const FloatingChromeProperties: React.FC<FloatingChromePropertiesProps> = ({
  element,
  position,
  onClose,
  onUpdate,
  onUpdateProperty,
}) => {
  const isIcon = element.category === "icon";
  const isAvatar = element.category === "avatar";
  const isGradient = element.category === "gradient";

  const title = isIcon
    ? "Icon Properties"
    : isAvatar
      ? "Avatar Properties"
      : "Gradient Properties";

  const icon = element.iconSettings ?? {};
  const avatar = element.avatarSettings ?? {};
  const gradient = element.gradientSettings ?? {};

  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">{title}</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[420px] overflow-y-auto">
        {/* Present on every panel so the control is reachable wherever the
            element opens its properties. */}
        <div className="flex items-center justify-between py-1">
          <label className="text-sm text-gray-800">Show in template only</label>
          <button
            onClick={() =>
              onUpdateProperty(
                "showInTemplateOnly",
                !element.properties?.showInTemplateOnly,
              )
            }
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.properties?.showInTemplateOnly
                ? "bg-[#EA33C0]"
                : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.properties?.showInTemplateOnly ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        {/* Fixed on screen: stays put when "… more" text expands and the rest
            of the screen is pushed down (status bar, tab bar, home indicator). */}
        <FixedOnScreenToggle
          pinned={Boolean(element.properties?.pinned)}
          onChange={(value) => onUpdateProperty("pinned", value)}
        />

        {isIcon && (
          <>
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-800">Colour</label>
              <input
                type="color"
                value={icon.color ?? "#000000"}
                onChange={(e) => onUpdate("color", e.target.value)}
                className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between py-1">
              <label className="text-sm text-gray-800">Filled</label>
              <button
                onClick={() => onUpdate("filled", !icon.filled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  icon.filled ? "bg-[#EA33C0]" : "bg-gray-300"
                }`}
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                    icon.filled ? "translate-x-6" : ""
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Stroke width ({icon.strokeWidth ?? 2})
              </label>
              <input
                type="range"
                min={1}
                max={4}
                step={0.5}
                value={icon.strokeWidth ?? 2}
                onChange={(e) =>
                  onUpdate("strokeWidth", Number(e.target.value))
                }
                className="w-full accent-[#EA33C0]"
              />
            </div>

            {/* Counters are how platforms show engagement beside a glyph. */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Count label
              </label>
              <input
                type="text"
                value={icon.count ?? ""}
                onChange={(e) => onUpdate("count", e.target.value)}
                placeholder="e.g. 1.2K"
                className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Count position
              </label>
              <select
                value={icon.countPosition ?? "none"}
                onChange={(e) => onUpdate("countPosition", e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
              >
                <option value="none">Hidden</option>
                <option value="left">Left of icon</option>
                <option value="right">Right of icon</option>
                <option value="top">Above icon</option>
                <option value="below">Below icon</option>
              </select>
            </div>
          </>
        )}

        {isAvatar && (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Shape
              </label>
              <select
                value={avatar.shape ?? "circle"}
                onChange={(e) => onUpdate("shape", e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
              >
                <option value="circle">Circle</option>
                <option value="rounded">Rounded square</option>
                <option value="square">Square</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Ring
              </label>
              <select
                value={avatar.ring ?? "none"}
                onChange={(e) => onUpdate("ring", e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
              >
                <option value="none">None</option>
                <option value="solid">Solid</option>
                <option value="gradient">Gradient</option>
              </select>
            </div>

            {avatar.ring === "solid" && (
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-800">Ring colour</label>
                <input
                  type="color"
                  value={avatar.ringColor ?? "#FF0069"}
                  onChange={(e) => onUpdate("ringColor", e.target.value)}
                  className="w-10 h-8 rounded border border-gray-300 cursor-pointer"
                />
              </div>
            )}

            {avatar.ring !== "none" && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Ring width ({avatar.ringWidth ?? 2}px)
                </label>
                <input
                  type="range"
                  min={1}
                  max={6}
                  value={avatar.ringWidth ?? 2}
                  onChange={(e) =>
                    onUpdate("ringWidth", Number(e.target.value))
                  }
                  className="w-full accent-[#EA33C0]"
                />
              </div>
            )}

            {/* Shown until an image is uploaded, so the design stays readable. */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Placeholder initials
              </label>
              <input
                type="text"
                value={avatar.initials ?? ""}
                onChange={(e) => onUpdate("initials", e.target.value)}
                maxLength={2}
                placeholder="AB"
                className="w-full px-3 py-2 border-2 border-[#EA33C0]/40 rounded-md text-sm focus:outline-none focus:border-[#EA33C0]"
              />
            </div>
          </>
        )}

        {isGradient && (
          <>
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-800">From</label>
              <input
                type="text"
                value={gradient.from ?? ""}
                onChange={(e) => onUpdate("from", e.target.value)}
                placeholder="rgba(0,0,0,0.75)"
                className="w-32 px-2 py-1.5 border-2 border-[#EA33C0]/40 rounded-md text-xs focus:outline-none focus:border-[#EA33C0]"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-800">To</label>
              <input
                type="text"
                value={gradient.to ?? ""}
                onChange={(e) => onUpdate("to", e.target.value)}
                placeholder="rgba(0,0,0,0)"
                className="w-32 px-2 py-1.5 border-2 border-[#EA33C0]/40 rounded-md text-xs focus:outline-none focus:border-[#EA33C0]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Angle ({gradient.angle ?? 180}°)
              </label>
              <input
                type="range"
                min={0}
                max={360}
                step={15}
                value={gradient.angle ?? 180}
                onChange={(e) => onUpdate("angle", Number(e.target.value))}
                className="w-full accent-[#EA33C0]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Opacity ({Math.round((gradient.opacity ?? 1) * 100)}%)
              </label>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round((gradient.opacity ?? 1) * 100)}
                onChange={(e) =>
                  onUpdate("opacity", Number(e.target.value) / 100)
                }
                className="w-full accent-[#EA33C0]"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Floating Shape Properties Component
interface FloatingShapePropertiesProps {
  element: any;
  position: { x: number; y: number };
  onClose: () => void;
  onUpdate: (key: string, value: any) => void;
  /** Updates a flag under `properties` (shared with the Properties panel). */
  onUpdateProperty: (key: string, value: any) => void;
}

const FloatingShapeProperties: React.FC<FloatingShapePropertiesProps> = ({
  element,
  position,
  onClose,
  onUpdate,
  onUpdateProperty,
}) => {
  return (
    <div
      className="absolute bg-white rounded-lg shadow-xl border-2 border-[#EA33C0] overflow-hidden z-50"
      style={{ left: position.x, top: position.y, width: "240px" }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-[#EA33C0]">
        <h3 className="font-bold text-white text-sm">Shape Properties</h3>
        <button onClick={onClose} className="text-white hover:text-gray-200">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        {/* Same show/hide control as the Properties panel — shapes open this
            panel instead, so it must be reachable here too. */}
        <div className="flex items-center justify-between py-2">
          <label className="text-sm text-gray-800">Show in template only</label>
          <button
            onClick={() =>
              onUpdateProperty(
                "showInTemplateOnly",
                !element.properties?.showInTemplateOnly,
              )
            }
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.properties?.showInTemplateOnly
                ? "bg-[#EA33C0]"
                : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.properties?.showInTemplateOnly ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        {/* Fixed on screen: stays put when "… more" text expands and the rest
            of the screen is pushed down (status bar, tab bar, home indicator). */}
        <FixedOnScreenToggle
          pinned={Boolean(element.properties?.pinned)}
          onChange={(value) => onUpdateProperty("pinned", value)}
        />

        <div className="flex items-center justify-between py-2">
          <label className="text-sm text-gray-800">Background</label>
          <button
            onClick={() =>
              onUpdate("background", !element.shapeSettings?.background)
            }
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.shapeSettings?.background ? "bg-[#EA33C0]" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.shapeSettings?.background ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between py-2">
          <label className="text-sm text-gray-800">Overlay</label>
          <button
            onClick={() => onUpdate("overlay", !element.shapeSettings?.overlay)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              element.shapeSettings?.overlay ? "bg-[#EA33C0]" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                element.shapeSettings?.overlay ? "translate-x-6" : ""
              }`}
            />
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Colour Picker
          </label>
          <input
            type="color"
            value={element.shapeSettings?.fill || "#EA33C0"}
            onChange={(e) => onUpdate("fill", e.target.value)}
            className="w-full h-10 border-2 border-[#EA33C0]/40 rounded-md cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};

// Layer Item Component
interface LayerItemProps {
  element: any;
  isSelected: boolean;
  onSelect: () => void;
  onToggleVisibility: () => void;
  onRename: (name: string) => void;
  index: number;
  isDragOver: boolean;
  onLayerDragStart: () => void;
  onLayerDragOver: (e: React.DragEvent) => void;
  onLayerDrop: () => void;
  onLayerDragEnd: () => void;
}

const LayerItem: React.FC<LayerItemProps> = ({
  element,
  isSelected,
  onSelect,
  onToggleVisibility,
  onRename,
  isDragOver,
  onLayerDragStart,
  onLayerDragOver,
  onLayerDrop,
  onLayerDragEnd,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(element.layerName || element.label);
  const inputRef = useRef<HTMLInputElement>(null);

  const getCategoryIcon = () => {
    switch (element.category) {
      case "text":
        return <Type size={14} />;
      case "image":
        return <ImageIcon size={14} />;
      case "video":
        return <Film size={14} />;
      case "audio":
        return <Music size={14} />;
      case "preview":
        return <FileText size={14} />;
      case "button":
        return <MousePointer size={14} />;
      case "shape":
        return <Square size={14} />;
      case "attributes":
        return <Square size={14} />;
      default:
        return <Square size={14} />;
    }
  };

  const handleStartEdit = () => {
    setEditName(element.layerName || element.label);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleConfirmRename = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== (element.layerName || element.label)) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancelRename = () => {
    setEditName(element.layerName || element.label);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirmRename();
    } else if (e.key === "Escape") {
      handleCancelRename();
    }
  };

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onLayerDragStart();
      }}
      onDragOver={onLayerDragOver}
      onDrop={(e) => {
        e.preventDefault();
        onLayerDrop();
      }}
      onDragEnd={onLayerDragEnd}
      className={`flex items-center gap-1.5 px-1 py-2 rounded-md cursor-pointer transition-colors group ${
        isSelected
          ? "bg-[#EA33C0]/10 border border-[#EA33C0]/30"
          : "hover:bg-gray-200/60 border border-transparent"
      } ${isDragOver ? "border-t-2 border-t-[#EA33C0]" : ""}`}
      onClick={() => {
        onSelect();
      }}
    >
      {/* Drag Handle */}
      <div
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#EA33C0] transition-colors"
        onMouseDown={(e) => e.stopPropagation()}
        title="Drag to reorder"
      >
        <GripVertical size={14} />
      </div>

      {/* Category Icon */}
      <div
        className={`flex-shrink-0 ${
          isSelected ? "text-[#EA33C0]" : "text-gray-500"
        }`}
      >
        {getCategoryIcon()}
      </div>

      {/* Layer Name */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleConfirmRename}
            onKeyDown={handleKeyDown}
            className="w-full text-xs bg-white border border-[#EA33C0]/40 rounded px-1.5 py-0.5 outline-none focus:border-[#EA33C0]"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`text-xs truncate block ${
              isSelected ? "text-[#EA33C0] font-semibold" : "text-black"
            } ${element.visible === false ? "opacity-40" : ""}`}
            onDoubleClick={(e) => {
              e.stopPropagation();
              handleStartEdit();
            }}
            title={element.layerName || element.label}
          >
            {element.layerName || element.label}
          </span>
        )}
      </div>

      {/* Edit Button */}
      {!isEditing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleStartEdit();
          }}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-[#EA33C0]"
          title="Rename layer"
        >
          <Pencil size={12} />
        </button>
      )}

      {/* Visibility Toggle */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisibility();
        }}
        className={`flex-shrink-0 transition-colors ${
          element.visible === false
            ? "text-gray-300 hover:text-gray-500"
            : "text-gray-500 hover:text-[#EA33C0]"
        }`}
        title={element.visible === false ? "Show layer" : "Hide layer"}
      >
        {element.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
};

export default AdBuilder;
